import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import "react-quill-new/dist/quill.snow.css";
import { SkeletonCell } from "../../components/SkeletonCell";
import SearchIcon from "../../assets/icons/SearchIcon";
import NotificationIcon from "../../assets/icons/NotificationIcon";
import CookieIcon from "../../assets/icons/CookieIcon";
import FacebookIcon from "../../assets/icons/FacebookIcon";
import LinkedInIcon from "../../assets/icons/LinkedInIcon";
import YoutubeIcon from "../../assets/icons/YoutubeIcon";
import TwitterIcon from "../../assets/icons/TwitterIcon";
import ProfileDropdown from "./ProfileDropdown";
import NotificationsDropdown from "./NotificationsDropdown";
import AuthPortalIllustration from "../../assets/images/auth-portal-illustration.png";
import AppLoader from "../../assets/images/appLoader.gif";
import { useDispatch, useSelector } from "react-redux";
import { selectAuth, selectIsLoggedIn, setAuth } from "../auth/authSlice";
import { authFetch } from "../../utils/authFetch";
import { forceLocalLogout } from "../auth/services/auth";
import NewXIcon from "../../assets/icons/NewXIcon";
import ChevronDownIcon from "../../assets/icons/ChevronDownIcon";
import LoginUserIcon from "../../assets/icons/LoginUserIcon";
import SummaryCardComponent from "../Summary/Summary";
import NoPrimaryOrganizationView from "./NoPrimaryOrganizationView";
import OrganizationDetails from "../organizations/OrganizationDetails/OrganizationDetails";
import ContactDetails from "../organizations/ContactDetails/ContactDetails";
import AccountSettings from "../accountSettings/AccountSettings";
import ForceLoginBar from "./ForceLoginBar";
import OnboardingAnnouncementBar from "./OnboardingAnnouncementBar";
// import { useGetSummaryDetailsQuery } from "../../api/portalApiSlice";

const LazySearchQueryRenderer = React.lazy(() =>
  import("../../components/PageBuilder/SearchQueryRenderer").then((module) => ({
    default: module.SearchQueryRenderer,
  }))
);

const LazyPublicDirectoryRenderer = React.lazy(() =>
  import("../Directories/Directory").then((module) => ({
    default: module.default,
  }))
);

const LazyFormFiller = React.lazy(() =>
  import("../../components/FormBuilder/CustomForm/FormFiller").then((module) => ({
    default: module.default,
  }))
);

const cloneExecutableScript = (source: HTMLScriptElement) => {
  const script = document.createElement("script");

  Array.from(source.attributes).forEach((attribute) => {
    script.setAttribute(attribute.name, attribute.value);
  });

  if (source.textContent) {
    script.textContent = source.textContent;
  }

  return script;
};

const firstNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) {
        return trimmed;
      }
    }
  }

  return undefined;
};

const normalizeBelongsTo = (value: unknown): 1 | 2 | undefined => {
  if (value === 1 || value === "1") {
    return 1;
  }

  if (value === 2 || value === "2") {
    return 2;
  }

  return undefined;
};

type PageSlug = "dashboard" | "about" | "events";

interface WebsiteTheme {
  primary: string;
  accent: string;
  neutral: string;
  textPrimary: string;
  textSecondary: string;
  navTextColor?: string;
  surfaceBase: string;
  surfaceCard: string;
  border: string;
  heroGradient: string;
  fontFamily: string;
  footerBackground: string;
  footerText: string;
}

interface NavigationItem {
  label: string;
  slug?: PageSlug;
  href?: string;
  subItems?: { label: string; pageUrl: string; href?: string; visibility?: string; openNewTab?: boolean }[];
  pageUrl?: string;
  visibility?: string;
  openNewTab?: boolean;
  requiredRoleIds?: number[];
}

interface UtilityLink {
  label: string;
  url?: string;
  visibility?: string;
  isExternal?: boolean;
  requiredRoleIds?: number[];
  openNewTab?: boolean;
}

interface NavigationProfile {
  name: string;
  status: string;
}

interface Announcement {
  prefix: string;
  message: string;
  ctaLabel: string;
}

interface HeroContent {
  eyebrow?: string;
  heading: string;
  description: string;
  primaryAction: string;
  secondaryAction?: string;
  image: string;
}

interface ServiceCard {
  title: string;
  description: string;
  emphasis: string;
}

interface HighlightBlock {
  title: string;
  description: string;
  image: string;
  emphasis: string;
}

interface DashboardPage {
  hero: HeroContent;
  services: {
    heading: string;
    description: string;
    cards: ServiceCard[];
  };
  highlights: HighlightBlock[];
  layoutRows?: PageRow[];
}

interface AboutStat {
  label: string;
  value: string;
}

interface AboutReason {
  heading: string;
  body: string;
}

interface AboutPage {
  hero: {
    title: string;
    breadcrumb: string[];
    image: string;
  };
  story: {
    heading: string;
    body: string;
    ctaLabel: string;
  };
  statsPanel: {
    heading: string;
    description: string;
    stats: AboutStat[];
  };
  focus: {
    heading: string;
    description: string;
    ctaLabel: string;
    image: string;
  };
  reasons: {
    heading: string;
    subtitle: string;
    items: AboutReason[];
  };
}

interface EventCard {
  title: string;
  location: string;
  city: string;
  blurb: string;
  priceLabel: string;
  image: string;
  attendees: string[];
}

interface LocalEvent {
  name: string;
  start: string;
  end: string;
  location: string;
  owner: string;
  fee: string;
}

interface EventsPage {
  hero: {
    title: string;
    breadcrumb: string[];
    image: string;
  };
  intro: {
    heading: string;
    description: string;
  };
  eventCards: EventCard[];
  stats: AboutStat[];
  localEvents: LocalEvent[];
}
interface PageElement {
  id: number;
  accessKey: string;
  position: number;
  belongsTo: number;
  config?: any;
}

interface PageRowSettings {
  bgColor?: string;
  mediaType?: string;
  imageUrl?: string;
  imageSignedUrl?: string;
  imageOverlayColor?: string;
  imageOverlayOpacity?: string;
}

interface PageRow {
  id: number;
  position: number;
  column: number;
  settings?: PageRowSettings;
  elements: PageElement[];
}

interface FooterLink {
  label: string;
  url?: string;
  isExternal?: boolean;
  openNewTab?: boolean;
  visibility?: string;
  requiredRoleIds?: number[];
}

interface FooterColumn {
  heading: string;
  items: FooterLink[];
}

interface FooterContent {
  summary: string;
  address: string;
  copyrightText?: string;
  columns: FooterColumn[];
  contact: {
    email: string;
    phone: string;
  };
  legal?: {
    termsOfService?: string;
    privacyPolicy?: string;
  };
  socialLinks?: { platform: string; label: string; url: string }[];
  logoUrl?: string;
  copyright?: string;
  showPrivacyPolicy?: boolean;
  showTermsAndConditions?: boolean;
}

interface WebsiteSeo {
  title?: string;
  description?: string;
  metaTags?: string[];
  image?: string;
  websiteTitle?: string;
  websiteDescription?: string;
  websiteMetaTags?: string[];
  websiteImage?: string;
  pageTitle?: string;
  pageDescription?: string;
  pageMetaTags?: string[];
  pageImage?: string;
}

interface WebsiteGlobal {
  css?: string;
  htmlHeader?: string;
  htmlFooter?: string;
  js?: string;
}

interface WebsiteDefinition {
  brand: {
    name: string;
    slogan: string;
    logoUrl?: string;
    faviconUrl?: string;
  };
  authContent?: {
    welcomeNote?: string;
    description?: string;
    logoUrl?: string;
  };
  announcement: Announcement;
  utilityLinks: UtilityLink[];
  navigation: {
    items: NavigationItem[];
    profile: NavigationProfile;
    ctaLabel: string;
    headerLayout?: string;
  };
  pages: {
    dashboard: DashboardPage;
    about: AboutPage;
    events: EventsPage;
  };
  footer: FooterContent;
  theme: WebsiteTheme;
  seo?: WebsiteSeo;
  global?: WebsiteGlobal;
  pageRequiresAuth?: boolean;
  pageAuthMessage?: string;
  loginUrl?: string;
  signUpUrl?: string;
  cookieConsentEnabled?: boolean;
}

const FOOTER_SOCIAL_PLATFORM_ORDER = [
  "facebook",
  "linkedin",
  "youtube",
  "twitter",
];
const defaultTheme: WebsiteTheme = {
  primary: "#0c4d8f",
  accent: "#ff7a45",
  neutral: "#ffffff",
  textPrimary: "#0f1c2c",
  textSecondary: "#4b5567",
  navTextColor: "#4b5567",
  surfaceBase: "transparent",
  surfaceCard: "#0556CB0D",
  border: "rgba(10, 38, 64, 0.12)",
  heroGradient: "none",
  fontFamily: "'Space Grotesk', 'Archivo', 'Segoe UI', sans-serif",
  footerBackground: "linear-gradient(120deg, #0f3b70, #071e3a)",
  footerText: "#ffffff",
};

const mapPageUrlToSlug = (url?: string): PageSlug | undefined => {
  if (!url) return undefined;
  const normalized = url.toLowerCase();
  if (normalized === "home" || normalized === "/" || normalized === "dashboard") {
    return "dashboard";
  }
  if (normalized === "about" || normalized === "/about") {
    return "about";
  }
  if (normalized === "events" || normalized === "/events") {
    return "events";
  }
  return undefined;
};

const extractFirstFormIdFromRows = (rows: PageRow[]): number | null => {
  for (const row of rows) {
    for (const element of row.elements || []) {
      if (element.accessKey !== "form") {
        continue;
      }

      const rawFormId = element.config?.formId ?? element.config?.formID;
      const parsed =
        typeof rawFormId === "number"
          ? rawFormId
          : typeof rawFormId === "string"
            ? Number(rawFormId)
            : NaN;

      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }
  }

  return null;
};

const isRestrictedVisibility = (visibility?: string): boolean => {
  if (!visibility) return false;
  const normalized = visibility.toLowerCase().replace(/[\s_-]/g, "");
  return normalized === "private" || normalized === "membersonly";
};

const normalizeVisibility = (visibility?: string): "public" | "membersonly" | "private" | "unknown" => {
  if (!visibility) return "public";
  const normalized = visibility.toLowerCase().replace(/[\s_-]/g, "");
  if (normalized === "public") return "public";
  if (normalized === "membersonly") return "membersonly";
  if (normalized === "private") return "private";
  return "unknown";
};

const parseOpenNewTab = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  return false;
};

const normalizeExternalUrl = (value?: string): string | undefined => {
  if (!value) return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  // Keep fully-qualified/special-scheme URLs unchanged.
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    return trimmed;
  }

  // Preserve root-relative links as-is.
  if (trimmed.startsWith("/")) {
    return trimmed;
  }

  // Treat bare domains as external URLs.
  return `https://${trimmed}`;
};

type PageRolesByUrl = Record<string, number[]>;

const fetchPageRolesMap = async (
  basePath: string,
  domain: string,
  pageUrls: string[]
): Promise<PageRolesByUrl> => {
  const pageParams = new URLSearchParams({ domain });
  const uniqueUrls = Array.from(new Set(pageUrls.filter(Boolean)));

  const entries = await Promise.all(
    uniqueUrls.map(async (url) => {
      try {
        const pagePath = `${basePath}/page/${encodeURIComponent(url)}`;
        const pageUrl = `${pagePath}?${pageParams.toString()}`;
        let response: Response;
        try {
          response = await authFetch(pageUrl);
        } catch {
          response = await fetch(pageUrl);
        }

        if (!response.ok) {
          return null;
        }

        const pageJson: any = await response.json();
        const pageData = pageJson?.page ?? pageJson;
        const roles = Array.isArray(pageData?.roles) ? pageData.roles : [];
        const roleIds = roles
          .map((r: any) => r?.id)
          .filter((id: unknown): id is number => typeof id === "number");

        return [url, roleIds] as const;
      } catch {
        return null;
      }
    })
  );

  const map: PageRolesByUrl = {};
  for (const entry of entries) {
    if (!entry) continue;
    const [url, roleIds] = entry;
    if (roleIds.length > 0) {
      map[url] = roleIds;
    }
  }
  return map;
};

const fetchWebsiteDefinitionFromApis = async (
  domain: string,
  url?: string
): Promise<WebsiteDefinition> => {
  const apiBase = process.env.REACT_APP_API_URL;

  if (!apiBase) {
    console.warn("REACT_APP_API_URL is not configured; website not reachable for preview.");
    throw new Error("Website not reachable.");
  }

  const basePath = `${apiBase}/public/site`;
  const domainParams = new URLSearchParams({ domain });

  try {
    // 1) Site status – decide if site exists
    const statusResponse = await fetch(
      `${basePath}/status?${domainParams.toString()}`
    );
    const statusJson: any = await statusResponse.json();

    if (!statusResponse.ok) {
      throw new Error("Failed to check website status.");
    }

    if (statusJson?.exists === false) {
      const fallbackMessage = statusJson?.fallback?.message ||
        "This website is not available.";
      throw new Error(fallbackMessage);
    }

    // 2) Shell – branding, colors, fonts, meta
    const shellResponse = await fetch(
      `${basePath}/shell?${domainParams.toString()}`
    );
    const shellJson: any = await shellResponse.json();

    if (!shellResponse.ok) {
      throw new Error("Failed to load site shell definition.");
    }

    // 3 & 4) Header + Footer – can load in parallel. If user is authenticated,
    // include Authorization header; otherwise fall back to anonymous requests.
    const headerUrl = `${basePath}/header?${domainParams.toString()}`;
    const footerUrl = `${basePath}/footer?${domainParams.toString()}`;

    let headerResponse: Response;
    let footerResponse: Response;

    try {
      [headerResponse, footerResponse] = await Promise.all([
        authFetch(headerUrl),
        authFetch(footerUrl),
      ]);
    } catch (_authError) {
      [headerResponse, footerResponse] = await Promise.all([
        fetch(headerUrl),
        fetch(footerUrl),
      ]);
    }

    const [headerJson, footerJson]: any[] = await Promise.all([
      headerResponse.json(),
      footerResponse.json(),
    ]);

    if (!headerResponse.ok) {
      throw new Error("Failed to load site header definition.");
    }

    if (!footerResponse.ok) {
      throw new Error("Failed to load site footer definition.");
    }

    // Collect all unique page URLs referenced in header/utility/footer menus
    const headerMenu = headerJson?.menu ?? [];
    const headerUtilityMenu = headerJson?.utilityMenu ?? [];
    const footerMenu = footerJson?.menu ?? [];

    const referencedPageUrls: string[] = [
      ...headerMenu.map((item: any) => item.page?.url || item.url || ""),
      ...headerUtilityMenu.map((item: any) => item.page?.url || item.url || ""),
      ...footerMenu.map((item: any) => item.page?.url || item.url || ""),
    ];

    const pageRolesByUrl = await fetchPageRolesMap(basePath, domain, referencedPageUrls);

    // 5) Page content – load after shell. If user is authenticated, include Authorization
    const pageParams = new URLSearchParams({ domain });
    const pagePath = url
      ? `${basePath}/page/${encodeURIComponent(url)}`
      : `${basePath}/page`;
    const pageUrl = `${pagePath}?${pageParams.toString()}`;

    let pageResponse: Response;
    try {
      pageResponse = await authFetch(pageUrl);
    } catch (authError) {
      // If not authenticated or authFetch fails, fall back to anonymous request
      pageResponse = await fetch(pageUrl);
    }
    const pageJson: any = await pageResponse.json();

    if (!pageResponse.ok) {
      throw new Error("Failed to load site page content.");
    }

    // Normalize: API returns either { exists, page: {...} } or the page object directly
    const pageData = pageJson?.page ?? pageJson;

    const pageRequiresAuth =
      (pageJson?.exists === false && pageJson?.requiresAuth) ||
      (pageData?.visibility && pageData.visibility !== "public");
    const pageAuthMessage = pageRequiresAuth
      ? pageJson?.message ||
      "This page requires authentication. Please log in to view this content."
      : undefined;

    if (pageRequiresAuth) {
      // eslint-disable-next-line no-console
      console.debug("Website preview: page requires auth, showing design-only screen", pageJson);
    }

    // Build a WebsiteDefinition by merging API-driven branding
    // into the existing mock structure used for layout.
    const branding = shellJson?.branding ?? {};
    const colors = shellJson?.colors ?? {};
    const fonts = shellJson?.fonts ?? {};
    const meta = shellJson?.meta ?? {};
    const shellSeo = shellJson?.seoSettings ?? {};
    const globalShellSettings = shellJson?.global ?? {};

    // headerMenu, headerUtilityMenu already defined above
    const headerStyling = {
      ...(headerJson?.styling ?? {}),
      ...(headerJson?.settings?.styleSettings?.styling ?? {}),
    };

    const footerBaseStyling = footerJson?.styling ?? {};
    const footerSettingsStyling = footerJson?.settings?.styleSettings ?? {};
    const footerStyling = {
      ...footerBaseStyling,
      ...footerSettingsStyling,
    };

    const themed: WebsiteTheme = {
      ...defaultTheme,
      // Primary color from shell API: main buttons, headers, links
      primary: colors.primary || headerStyling.textColor || defaultTheme.primary,
      // Secondary color from shell API: subheadings, borders, icons
      accent: colors.secondary || defaultTheme.accent,
      neutral: defaultTheme.neutral,
      textPrimary: colors.text || headerStyling.textColor || defaultTheme.textPrimary,
      textSecondary: colors.secondary || defaultTheme.textSecondary,
      navTextColor: headerStyling.textColor || defaultTheme.navTextColor,
      surfaceBase: defaultTheme.surfaceBase,
      surfaceCard: headerStyling.backgroundColor || defaultTheme.surfaceCard,
      border: defaultTheme.border,
      heroGradient: defaultTheme.heroGradient,
      fontFamily: fonts.primary || defaultTheme.fontFamily,
      footerBackground: footerStyling.backgroundColor || defaultTheme.footerBackground,
      footerText: footerStyling.textColor || defaultTheme.footerText,
    };

    const navigationItems: NavigationItem[] = headerMenu.length
      ? headerMenu.map((item: any) => {
        const pageUrl: string = item.page?.url || item.url || "";
        const label: string = item.page?.name || item.label || "";
        const isExternal: boolean =
          Boolean(item.isExternal) ||
          item.pageType === "external" ||
          item.page?.pageType === "external";
        const slug = !isExternal ? mapPageUrlToSlug(pageUrl) : undefined;
        const visibility = item.page?.visibility || item.visibility;
        const roleIds = pageRolesByUrl[pageUrl] || [];
        const openNewTab = parseOpenNewTab(item.openNewTab ?? item.page?.openNewTab);
        return {
          label,
          slug,
          href: isExternal ? pageUrl : undefined,
          pageUrl: !isExternal ? pageUrl : undefined,
          visibility,
          requiredRoleIds: roleIds,
          openNewTab,
          subItems: Array.isArray(item.children) && item.children.length
            ? item.children.map((child: any) => {
              const childPageUrl: string = child.page?.url || child.url || "";
              const childIsExternal: boolean =
                Boolean(child.isExternal) ||
                child.pageType === "external" ||
                child.page?.pageType === "external";

              return {
                label: child.page?.name || child.label || "",
                pageUrl: childPageUrl,
                href: childIsExternal ? childPageUrl : undefined,
                visibility: child.page?.visibility || child.visibility,
                openNewTab: parseOpenNewTab(child.openNewTab ?? child.page?.openNewTab),
              };
            })
            : undefined,
        };
      })
      : [];

    const utilityLinks: UtilityLink[] = headerUtilityMenu.length
      ? headerUtilityMenu
        .map((item: any) => {
          const pageUrl: string = item.page?.url || item.url || "";
          const visibility = item.page?.visibility || item.visibility;
          const roleIds = pageRolesByUrl[pageUrl] || [];
          const openNewTab = parseOpenNewTab(item.openNewTab ?? item.page?.openNewTab);
          const isExternal =
            Boolean(item.isExternal) ||
            item.pageType === "external" ||
            item.page?.pageType === "external";

          return {
            label: item.page?.name || item.label || "",
            url: pageUrl,
            visibility,
            isExternal,
            requiredRoleIds: roleIds,
            openNewTab,
          };
        })
        .filter((item: UtilityLink) => !!item.label)
      : [];

    const footerMenuItems: FooterLink[] = Array.isArray(footerJson?.menu)
      ? footerJson.menu
        .map((item: any) => {
          const pageUrl: string = item.page?.url || item.url || "";
          const visibility = item.page?.visibility || item.visibility;
          const roleIds = pageRolesByUrl[pageUrl] || [];
          const openNewTab = parseOpenNewTab(item.openNewTab ?? item.page?.openNewTab);
          const isExternal =
            Boolean(item.isExternal) ||
            item.pageType === "external" ||
            item.page?.pageType === "external";

          return {
            label: item.page?.name || item.label || "",
            url: pageUrl,
            isExternal,
            openNewTab,
            visibility,
            requiredRoleIds: roleIds,
          };
        })
        .filter((link: FooterLink) => !!link.label)
      : [];

    // Social media — support both flat string map (footerJson.socialMedia)
    // and the nested object map (settings.styleSettings.socialMedia)
    const flatSocialMedia: Record<string, string> = footerJson?.socialMedia ?? {};
    const nestedSocialMedia: Record<string, any> = footerJson?.settings?.styleSettings?.socialMedia ?? {};

    const footerSocialLinks: { platform: string; label: string; url: string }[] = [
      ...Object.entries(flatSocialMedia).map(([key, rawUrl]) => {
        if (!rawUrl || typeof rawUrl !== "string") return null;
        const url = rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`;
        return { platform: key.toLowerCase(), label: key.charAt(0).toUpperCase() + key.slice(1), url };
      }),
      ...Object.entries(nestedSocialMedia)
        .filter(([key]) => !(key in flatSocialMedia)) // avoid duplicates
        .map(([key, entry]) => {
          if (!entry?.url || entry.enabled === false) return null;
          const url = entry.url.startsWith("http") ? entry.url : `https://${entry.url}`;
          return { platform: key.toLowerCase(), label: key.charAt(0).toUpperCase() + key.slice(1), url };
        }),
    ].filter((link): link is { platform: string; label: string; url: string } => !!link);

    const pageMeta = pageData?.seoSettings ?? {};

    const rowsJson = pageData?.rows ?? [];

    const layoutRows: PageRow[] = Array.isArray(rowsJson)
      ? rowsJson
        .filter((row: any) => row.statusId === 16)
        .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
        .map((row: any) => ({
          id: row.id,
          position: row.position,
          column: row.column,
          settings: row.settings || {},
          elements: Array.isArray(row.elements)
            ? row.elements
              .filter((el: any) => el.statusId === 16)
              .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
              .map((el: any) => ({
                id: el.id,
                accessKey: el.accessKey,
                position: el.position,
                belongsTo: el.belongsTo,
                config: el.settings?.config,
              }))
            : [],
        }))
      : [];

    const websiteTitle: string =
      meta.title || branding.companyName || pageData?.name || "";

    const websiteDescription: string =
      shellSeo.metaDescription ||
      meta.description ||
      "";

    const websiteMetaTags: string[] =
      (Array.isArray(shellSeo.metaTags) && shellSeo.metaTags.length
        ? shellSeo.metaTags
        : Array.isArray(meta.keywords)
          ? meta.keywords
          : typeof meta.keywords === "string" && meta.keywords
            ? [meta.keywords]
            : []);

    const websiteImage: string =
      firstNonEmptyString(
        shellSeo.metaImage,
        shellSeo.image,
        shellSeo.ogImage,
        shellSeo.twitterImage,
        meta.image,
        branding.logoUrl,
        branding.faviconUrl,
        branding.favicon
      ) || "";

    const pageTitle: string = pageData?.name || "";
    const pageDescription: string = pageMeta.metaDescription || "";
    const pageMetaTags: string[] =
      Array.isArray(pageMeta.metaTags) ? pageMeta.metaTags : [];
    const pageImage: string =
      firstNonEmptyString(
        pageMeta.metaImage,
        pageMeta.image,
        pageMeta.ogImage,
        pageMeta.twitterImage
      ) || "";

    const resolvedTitle: string = pageTitle || websiteTitle;
    const resolvedDescription: string = pageDescription || websiteDescription;
    const resolvedMetaTags: string[] =
      pageMetaTags.length ? pageMetaTags : websiteMetaTags;
    const resolvedImage: string = pageImage || websiteImage;

    let definition: WebsiteDefinition = {
      brand: {
        name: resolvedTitle,
        slogan: branding.companyName || headerJson?.logo?.alt || "",
        logoUrl: headerJson?.logo?.url || branding.logoUrl || "",
        faviconUrl:
          branding.faviconUrl ||
          branding.favicon ||
          meta.faviconUrl ||
          meta.favicon ||
          "",
      },
      authContent: {
        welcomeNote: firstNonEmptyString(shellJson?.welcomeNote),
        description: firstNonEmptyString(shellJson?.description),
        logoUrl: firstNonEmptyString(branding.logoUrl, branding.logo),
      },
      announcement: {
        prefix: "",
        message: "",
        ctaLabel: "",
      },
      utilityLinks,
      navigation: {
        items: navigationItems,
        profile: {
          name: "",
          status: "",
        },
        ctaLabel: headerJson?.loginEnabled ? "Login" : "",
        headerLayout: headerJson?.settings?.styleSettings?.layout?.type || headerStyling.layout || "left-right",
      },
      pages: {
        dashboard: {
          hero: {
            eyebrow: "",
            heading: resolvedTitle,
            description: resolvedDescription,
            primaryAction: headerJson?.loginEnabled ? "Login" : "",
            secondaryAction: "",
            image: branding.logoUrl || "",
          },
          services: {
            heading: "",
            description: "",
            cards: [],
          },
          highlights: [],
          layoutRows,
        },
        about: {
          hero: {
            title: "",
            breadcrumb: [],
            image: "",
          },
          story: {
            heading: "",
            body: "",
            ctaLabel: "",
          },
          statsPanel: {
            heading: "",
            description: "",
            stats: [],
          },
          focus: {
            heading: "",
            description: "",
            ctaLabel: "",
            image: "",
          },
          reasons: {
            heading: "",
            subtitle: "",
            items: [],
          },
        },
        events: {
          hero: {
            title: "",
            breadcrumb: [],
            image: "",
          },
          intro: {
            heading: "",
            description: "",
          },
          eventCards: [],
          stats: [],
          localEvents: [],
        },
      },
      footer: {
        summary:
          footerJson?.settings?.html?.content?.overview ||
          meta.description ||
          "",
        address:
          footerJson?.settings?.html?.content?.address || "",
        copyrightText: footerJson?.settings?.html?.content?.copyrightText || `Copyright © ${new Date().getFullYear()}. All Rights Reserved.`,
        contact: {
          email: "",
          phone: "",
        },
        columns: footerMenuItems.length
          ? [
            {
              heading: "",
              items: footerMenuItems,
            },
          ]
          : [],
        legal: footerJson?.legal,
        socialLinks: footerSocialLinks as { platform: string; label: string; url: string }[],
        logoUrl:
          footerJson?.logo?.url ||
          headerJson?.logo?.url ||
          branding.logoUrl || "",
        // copyright: footerJson?.copyright,
        copyright: `Copyright © ${new Date().getFullYear()}. All Rights Reserved.`,
        showPrivacyPolicy: footerJson?.settings?.html?.content?.showPrivacyPolicy,
        showTermsAndConditions:
          footerJson?.settings?.html?.content?.showTermsAndConditions,
      },
      theme: themed,
      seo: {
        title: resolvedTitle,
        description: resolvedDescription,
        metaTags: resolvedMetaTags,
        image: resolvedImage,
        websiteTitle,
        websiteDescription,
        websiteMetaTags,
        websiteImage,
        pageTitle,
        pageDescription,
        pageMetaTags,
        pageImage,
      },
      global: {
        css: typeof globalShellSettings.css === "string" ? globalShellSettings.css : undefined,
        htmlHeader:
          typeof globalShellSettings.html?.header === "string"
            ? globalShellSettings.html.header
            : undefined,
        htmlFooter:
          typeof globalShellSettings.html?.footer === "string"
            ? globalShellSettings.html.footer
            : undefined,
        js: typeof globalShellSettings.js === "string" ? globalShellSettings.js : undefined,
      },
      pageRequiresAuth,
      pageAuthMessage,
      loginUrl: headerJson?.loginUrl,
      signUpUrl: headerJson?.signupUrl,
      cookieConsentEnabled: shellJson?.cookieConsent?.enabled === true,
    };

    // If public page content is available, lightly reflect it in the hero
    // area so preview feels connected to the actual page definition.
    // pageJson is already reflected in the initial definition build for dashboard hero.

    // eslint-disable-next-line no-console
    // console.debug("Website preview API responses (mapped)", {
    //   status: statusJson,
    //   shell: shellJson,
    //   header: headerJson,
    //   footer: footerJson,
    //   page: pageJson,
    // });

    return definition;
  } catch (error) {
    console.error("Failed to fetch website definition from APIs", error);
    if (error instanceof Error && !error.message.startsWith("Failed to ")) {
      throw error;
    }
    throw new Error("Website not reachable.");
  }
};

const fetchPageLayoutForUrl = async (
  domain: string,
  url?: string
): Promise<{
  layoutRows: PageRow[];
  pageRequiresAuth?: boolean;
  pageAuthMessage?: string;
  pageTitle?: string;
  pageDescription?: string;
  pageMetaTags?: string[];
  pageImage?: string;
}> => {
  const apiBase = process.env.REACT_APP_API_URL;

  if (!apiBase) {
    console.warn("REACT_APP_API_URL is not configured; website not reachable for preview.");
    throw new Error("Website not reachable.");
  }

  const basePath = `${apiBase}/public/site`;
  const pageParams = new URLSearchParams({ domain });
  const pagePath = url
    ? `${basePath}/page/${encodeURIComponent(url)}`
    : `${basePath}/page`;

  const pageUrl = `${pagePath}?${pageParams.toString()}`;

  let pageResponse: Response;
  try {
    pageResponse = await authFetch(pageUrl);
  } catch (authError) {
    // If not authenticated or authFetch fails, fall back to anonymous request
    pageResponse = await fetch(pageUrl);
  }
  const pageJson: any = await pageResponse.json();

  if (!pageResponse.ok) {
    throw new Error("Failed to load site page content.");
  }

  // Normalize: API returns either { exists, page: {...} } or the page object directly
  const pageData = pageJson?.page ?? pageJson;

  const pageRequiresAuth =
    (pageJson?.exists === false && pageJson?.requiresAuth) ||
    (pageData?.visibility && pageData.visibility !== "public");
  const pageAuthMessage = pageRequiresAuth
    ? pageJson?.message ||
    "This page requires authentication. Please log in to view this content."
    : undefined;

  if (pageRequiresAuth) {
    // eslint-disable-next-line no-console
    console.debug("Website preview: page requires auth, showing design-only screen", pageJson);
  }

  const pageMeta = pageData?.seoSettings ?? {};
  const rowsJson = pageData?.rows ?? [];

  const layoutRows: PageRow[] = Array.isArray(rowsJson)
    ? rowsJson
      .filter((row: any) => row.statusId === 16)
      .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
      .map((row: any) => ({
        id: row.id,
        position: row.position,
        column: row.column,
        settings: row.settings || {},
        elements: Array.isArray(row.elements)
          ? row.elements
            .filter((el: any) => el.statusId === 16)
            .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
            .map((el: any) => ({
              id: el.id,
              accessKey: el.accessKey,
              position: el.position,
              belongsTo: el.belongsTo,
              config: el.settings?.config,
            }))
          : [],
      }))
    : [];

  return {
    layoutRows,
    pageRequiresAuth,
    pageAuthMessage,
    pageTitle: pageData?.name,
    pageDescription: pageMeta.metaDescription,
    pageMetaTags: Array.isArray(pageMeta.metaTags)
      ? pageMeta.metaTags
      : undefined,
    pageImage: firstNonEmptyString(
      pageMeta.metaImage,
      pageMeta.image,
      pageMeta.ogImage,
      pageMeta.twitterImage
    ),
  };
};

const WebsitePreview = () => {
  const COOKIE_CONSENT_STORAGE_PREFIX = "portal_cookie_consent";
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [definition, setDefinition] = useState<WebsiteDefinition | null>(null);
  console.log(definition, 'definition');

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasPrimaryOrganization, setHasPrimaryOrganization] = useState<boolean | null>(null);
  const [forceShowNoPrimaryOrganizationView, setForceShowNoPrimaryOrganizationView] = useState(false);
  const [contactProfileCompleted, setContactProfileCompleted] = useState<boolean | null>(null);
  const [profileCompletionPercentage, setProfileCompletionPercentage] = useState<number | null>(null);
  const [contactOnboardingFormId, setContactOnboardingFormId] = useState<number | null>(null);
  const [isContactOnboardingFormLoading, setIsContactOnboardingFormLoading] = useState(false);
  const [userRoleIds, setUserRoleIds] = useState<number[]>([]);
  const [activePage, setActivePage] = useState<PageSlug>("dashboard");
  const [hoveredNav, setHoveredNav] = useState<string | null>(null);
  const [activeNavLabel, setActiveNavLabel] = useState<string | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const navActionsRef = useRef<HTMLDivElement | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState<string | undefined>(undefined);

  const auth = useSelector(selectAuth);
  const latestAuthRef = useRef(auth);
  const isMeFetchInFlightRef = useRef(false);
  const lastMeFetchAtRef = useRef(0);

  const isLoggedIn = useSelector(selectIsLoggedIn);

  useEffect(() => {
    latestAuthRef.current = auth;
  }, [auth]);

  const profileDisplayName = isLoggedIn
    ? auth.fullName ||
    [auth.firstName, auth.lastName].filter(Boolean).join(" ") ||
    auth.email ||
    "Guest user"
    : "Login/SignUp";

  const profileEmail = auth.email || undefined;

  const getDomainFromQuery = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const domainFromQuery = searchParams.get("domain");
    return domainFromQuery ? decodeURIComponent(domainFromQuery) : window.location.origin;
  };

  const getPageFromQuery = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const pageFromQuery = searchParams.get("page");
    return pageFromQuery ? decodeURIComponent(pageFromQuery) : undefined;
  };

  const normalizePageKey = (pageValue?: string) => {
    const normalized = (pageValue || "").toLowerCase();
    const internalAppPages = ["organizationdetails", "contactdetails", "settings"];
    return internalAppPages.includes(normalized) ? "" : normalized;
  };

  const queryPage = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageFromQuery = searchParams.get("page");
    return pageFromQuery ? decodeURIComponent(pageFromQuery).toLowerCase() : "";
  }, [location.search]);

  const syncPageInPreviewUrl = (pageUrl?: string, pageId?: string) => {
    const searchParams = new URLSearchParams(window.location.search);

    if (pageUrl) {
      searchParams.set("page", pageUrl);
    } else {
      searchParams.delete("page");
    }

    const normalizedPage = (pageUrl || "").toLowerCase();
    const idRequired = normalizedPage === "organizationdetails" || normalizedPage === "contactdetails";

    if (idRequired && pageId) {
      searchParams.set("id", pageId);
    } else {
      searchParams.delete("id");
    }

    const nextQuery = searchParams.toString();
    const nextSearch = nextQuery ? `?${nextQuery}` : "";

    if (nextSearch !== window.location.search) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch,
        },
        { replace: false }
      );
    }
  };

  const profileInitials = React.useMemo(() => {
    const source = profileDisplayName || "";
    const parts = source.trim().split(" ");
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }, [profileDisplayName]);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hasId = searchParams.has("id");
    const idRequired = queryPage === "organizationdetails" || queryPage === "contactdetails";

    if (!hasId || idRequired) {
      return;
    }

    searchParams.delete("id");
    const nextQuery = searchParams.toString();
    const nextSearch = nextQuery ? `?${nextQuery}` : "";

    if (nextSearch !== window.location.search) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch,
        },
        { replace: true }
      );
    }
  }, [queryPage, navigate, location.pathname]);

  const refetchMe = React.useCallback(async () => {
    if (!isLoggedIn) {
      if (!isMountedRef.current) return;
      setProfileImageUrl(undefined);
      setHasPrimaryOrganization(null);
      setContactProfileCompleted(null);
      setProfileCompletionPercentage(null);
      return;
    }

    const now = Date.now();
    if (isMeFetchInFlightRef.current) {
      return;
    }

    // Coalesce rapid duplicate triggers from chained state updates/effects.
    if (now - lastMeFetchAtRef.current < 1000) {
      return;
    }

    isMeFetchInFlightRef.current = true;
    lastMeFetchAtRef.current = now;

    try {
      const apiBase = process.env.REACT_APP_API_URL;
      if (!apiBase) {
        if (!isMountedRef.current) return;
        setHasPrimaryOrganization(true);
        return;
      }

      const response = await authFetch(`${apiBase}/portal/me`);
      const json: any = await response.json();

      if (!response.ok) {
        // Any error from /me should clear local storage and log the user out.
        forceLocalLogout();
        return;
      }

      if (!isMountedRef.current) return;

      const rawProfileUrl = json?.data?.signedUrl || json?.signedUrl;
      if (rawProfileUrl) {
        const cacheSource = json?.data?.updatedAt || json?.updatedAt || Date.now();
        const cacheKey = typeof cacheSource === "string" ? cacheSource : String(cacheSource);

        const hashIndex = rawProfileUrl.indexOf("#");
        let cacheBustedUrl: string;
        if (hashIndex >= 0) {
          const base = rawProfileUrl.slice(0, hashIndex);
          const existingHash = rawProfileUrl.slice(hashIndex + 1);
          const sep = existingHash ? "&" : "";
          cacheBustedUrl = `${base}#${existingHash}${sep}v=${encodeURIComponent(cacheKey)}`;
        } else {
          cacheBustedUrl = `${rawProfileUrl}#v=${encodeURIComponent(cacheKey)}`;
        }

        setProfileImageUrl(cacheBustedUrl);
      } else {
        setProfileImageUrl(undefined);
      }
      setHasPrimaryOrganization(Boolean(json?.data?.primaryOrganization));

      const user = json?.data;
      const currentAuth = latestAuthRef.current;
      const resolvedOrganizationId = user?.primaryOrganization?.id ?? currentAuth?.organizationId ?? null;

      const nextAuth = {
        ...currentAuth,
        email: user?.email ?? currentAuth?.email ?? null,
        userId: user?.id ?? currentAuth?.userId ?? null,
        firstName: user?.firstName ?? currentAuth?.firstName ?? null,
        lastName: user?.lastName ?? currentAuth?.lastName ?? null,
        fullName: user?.fullName ?? currentAuth?.fullName ?? null,
        organizationId: resolvedOrganizationId,
      };

      const authNeedsUpdate =
        nextAuth.organizationId !== currentAuth?.organizationId ||
        nextAuth.userId !== currentAuth?.userId ||
        nextAuth.email !== currentAuth?.email ||
        nextAuth.firstName !== currentAuth?.firstName ||
        nextAuth.lastName !== currentAuth?.lastName ||
        nextAuth.fullName !== currentAuth?.fullName;

      if (authNeedsUpdate) {
        dispatch(setAuth(nextAuth as any));
      }

      const completed =
        json?.data?.contactProfileCompleted ?? json?.contactProfileCompleted;
      const pctRaw =
        json?.data?.profileCompletionPercentage ?? json?.profileCompletionPercentage ?? null;

      setContactProfileCompleted(
        typeof completed === "boolean" ? completed : null
      );

      if (typeof pctRaw === "number" && !Number.isNaN(pctRaw)) {
        setProfileCompletionPercentage(Math.max(0, Math.min(100, pctRaw)));
      } else {
        setProfileCompletionPercentage(null);
      }

      const allRoles =
        json?.data?.allRoles ||
        json?.allRoles ||
        json?.data?.primaryOrganizationRoles ||
        json?.primaryOrganizationRoles ||
        [];

      const ids = Array.isArray(allRoles)
        ? allRoles
          .map((r: any) => r?.id)
          .filter((id: unknown): id is number => typeof id === "number")
        : [];
      setUserRoleIds(ids);
    } catch {
      // Network or other unexpected errors from /me should also force a logout.
      forceLocalLogout();
    } finally {
      isMeFetchInFlightRef.current = false;
    }
  }, [isLoggedIn, dispatch]);

  useEffect(() => {
    void refetchMe();
  }, [refetchMe]);

  useEffect(() => {
    const handleOnboardingComplete = () => {
      void refetchMe();
    };

    window.addEventListener("portal-contact-onboarding-completed", handleOnboardingComplete);
    return () => {
      window.removeEventListener("portal-contact-onboarding-completed", handleOnboardingComplete);
    };
  }, [refetchMe]);

  useEffect(() => {
    let isMounted = true;

    const loadWebsite = async () => {
      const domain = getDomainFromQuery();
      const pageFromQueryRaw = getPageFromQuery();
      const requestedPageKey = normalizePageKey(pageFromQueryRaw);
      const pageFromQuery = requestedPageKey ? pageFromQueryRaw : undefined;

      try {
        const response = await fetchWebsiteDefinitionFromApis(domain, pageFromQuery);

        if (!isMounted) return;

        // Ignore stale responses if URL page changed while request was in flight.
        const currentPageKey = normalizePageKey(getPageFromQuery());
        if (requestedPageKey !== currentPageKey) {
          return;
        }

        setDefinition(response);
        let hasStoredConsent = false;
        try {
          const storageKey = `${COOKIE_CONSENT_STORAGE_PREFIX}_${domain}`;
          const savedConsent = localStorage.getItem(storageKey);
          hasStoredConsent = savedConsent === "accepted" || savedConsent === "rejected";
        } catch {
          hasStoredConsent = false;
        }

        setShowCookieConsent(response.cookieConsentEnabled === true && !hasStoredConsent);

        if (pageFromQuery) {
          const matchingNavItem = response.navigation.items.find((navItem) =>
            navItem.pageUrl === pageFromQuery
          );

          if (matchingNavItem) {
            setActiveNavLabel(matchingNavItem.label);
          }

          const mappedSlug = mapPageUrlToSlug(pageFromQuery);
          if (mappedSlug) {
            setActivePage(mappedSlug);
          }
        }
      } catch (err) {
        if (!isMounted) return;
        setError(
          err instanceof Error && !err.message.startsWith("Failed to ")
            ? err.message
            : "Website not reachable."
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadWebsite();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!navActionsRef.current) return;
      if (!navActionsRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Ensure the chosen brand font from L2 site builder is actually loaded on L3
  useEffect(() => {
    if (!definition?.theme?.fontFamily) return;

    const raw = definition.theme.fontFamily.split(",")[0].trim();
    const primaryFont = raw.replace(/^['"]|['"]$/g, "");

    const googleFontUrls: Record<string, string> = {
      Roboto: "https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap",
      Montserrat: "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap",
      Poppins: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap",
    };

    const fontUrl = googleFontUrls[primaryFont];
    const linkId = "portal-dynamic-font-family";
    const existing = document.getElementById(linkId) as HTMLLinkElement | null;

    if (!fontUrl) {
      // If current selection is a system font (e.g. Times New Roman), remove any dynamic link
      if (existing && existing.parentNode) {
        existing.parentNode.removeChild(existing);
      }
      return;
    }

    if (existing) {
      if (existing.href !== fontUrl) {
        existing.href = fontUrl;
      }
      return;
    }

    const link = document.createElement("link");
    link.id = linkId;
    link.rel = "stylesheet";
    link.href = fontUrl;
    document.head.appendChild(link);
  }, [definition?.theme?.fontFamily]);

  // Keep browser tab title and favicon in sync with the current site/page
  useEffect(() => {
    if (!definition) return;

    const websiteSeoTitle = definition.seo?.websiteTitle || definition.brand.name;
    const websiteSeoDescription = definition.seo?.websiteDescription || definition.seo?.description;
    const websiteSeoMetaTags = definition.seo?.websiteMetaTags || [];
    const websiteSeoImage =
      definition.seo?.websiteImage ||
      definition.seo?.image ||
      definition.brand.logoUrl ||
      definition.brand.faviconUrl;

    const currentPageSeoTitle =
      activePage === "dashboard"
        ? definition.seo?.pageTitle
        : undefined;

    const currentPageSeoDescription =
      activePage === "dashboard"
        ? definition.seo?.pageDescription
        : undefined;

    const currentPageSeoMetaTags =
      activePage === "dashboard"
        ? definition.seo?.pageMetaTags
        : undefined;
    const currentPageSeoImage =
      activePage === "dashboard"
        ? definition.seo?.pageImage
        : undefined;

    const appliedTitle =
      currentPageSeoTitle || websiteSeoTitle || "AMS L3 Portal";
    const appliedDescription =
      currentPageSeoDescription || websiteSeoDescription;
    const keywordsArray =
      currentPageSeoMetaTags && currentPageSeoMetaTags.length
        ? currentPageSeoMetaTags
        : websiteSeoMetaTags;
    const rawImage = currentPageSeoImage || websiteSeoImage;
    const appliedImage = rawImage
      ? (() => {
        try {
          return new URL(rawImage, window.location.origin).toString();
        } catch {
          return rawImage;
        }
      })()
      : undefined;
    const appliedUrl = window.location.href;

    document.title = appliedTitle;

    const keywordsContent =
      keywordsArray && keywordsArray.length ? keywordsArray.join(", ") : "";

    const ensureMetaTag = (
      id: string,
      name: string,
      content: string | undefined
    ) => {
      const trimmed = content ? content.trim() : "";
      let tag = document.getElementById(id) as HTMLMetaElement | null;

      // Reuse an existing meta[name=...] (e.g. CRA template tag) to prevent duplicates.
      if (!tag) {
        tag = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
        if (tag && !tag.id) {
          tag.id = id;
        }
      }

      if (!trimmed) {
        if (tag && tag.parentNode) {
          tag.parentNode.removeChild(tag);
        }
        return;
      }

      if (!tag) {
        tag = document.createElement("meta");
        tag.id = id;
        tag.name = name;
        document.head.appendChild(tag);
      } else if (tag.name !== name) {
        tag.name = name;
      }

      if (tag.content !== trimmed) {
        tag.content = trimmed;
      }
    };

    ensureMetaTag(
      "portal-dynamic-meta-description",
      "description",
      appliedDescription
    );

    ensureMetaTag(
      "portal-dynamic-meta-keywords",
      "keywords",
      keywordsContent
    );

    ensureMetaTag(
      "portal-dynamic-meta-title",
      "title",
      appliedTitle
    );

    const ensureMetaPropertyTag = (
      id: string,
      property: string,
      content: string | undefined
    ) => {
      const trimmed = content ? content.trim() : "";
      let tag = document.getElementById(id) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.head.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
        if (tag && !tag.id) {
          tag.id = id;
        }
      }

      if (!trimmed) {
        if (tag && tag.parentNode) {
          tag.parentNode.removeChild(tag);
        }
        return;
      }

      if (!tag) {
        tag = document.createElement("meta");
        tag.id = id;
        document.head.appendChild(tag);
      }

      if (tag.getAttribute("property") !== property) {
        tag.setAttribute("property", property);
      }

      if (tag.content !== trimmed) {
        tag.content = trimmed;
      }
    };

    ensureMetaPropertyTag("portal-dynamic-og-type", "og:type", "website");
    ensureMetaPropertyTag("portal-dynamic-og-url", "og:url", appliedUrl);
    ensureMetaPropertyTag("portal-dynamic-og-title", "og:title", appliedTitle);
    ensureMetaPropertyTag("portal-dynamic-og-description", "og:description", appliedDescription);
    ensureMetaPropertyTag("portal-dynamic-og-image", "og:image", appliedImage);

    ensureMetaPropertyTag(
      "portal-dynamic-twitter-card",
      "twitter:card",
      appliedImage ? "summary_large_image" : "summary"
    );
    ensureMetaPropertyTag("portal-dynamic-twitter-url", "twitter:url", appliedUrl);
    ensureMetaPropertyTag("portal-dynamic-twitter-title", "twitter:title", appliedTitle);
    ensureMetaPropertyTag("portal-dynamic-twitter-description", "twitter:description", appliedDescription);
    ensureMetaPropertyTag("portal-dynamic-twitter-image", "twitter:image", appliedImage);

    const faviconUrl = definition.brand.faviconUrl || definition.brand.logoUrl;
    if (faviconUrl) {
      const linkId = "portal-dynamic-favicon";
      let link = document.getElementById(linkId) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.id = linkId;
        link.rel = "icon";
        document.head.appendChild(link);
      }
      if (link.href !== faviconUrl) {
        link.href = faviconUrl;
      }
    }
  }, [definition, activePage]);

  // Inject global CSS from L2 shell
  useEffect(() => {
    const css = definition?.global?.css;
    const styleId = "portal-global-css";
    let styleTag = document.getElementById(styleId) as HTMLStyleElement | null;

    if (!css || !css.trim()) {
      if (styleTag && styleTag.parentNode) {
        styleTag.parentNode.removeChild(styleTag);
      }
      return;
    }

    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    if (styleTag.textContent !== css) {
      styleTag.textContent = css;
    }
  }, [definition?.global?.css]);

  // Inject arbitrary head HTML from L2 shell (meta/link/etc.)
  useEffect(() => {
    const headerHtml = definition?.global?.htmlHeader;
    const existing = Array.from(
      document.head.querySelectorAll('[data-portal-global-head="true"]')
    );

    existing.forEach((node) => {
      if (node.parentNode) {
        node.parentNode.removeChild(node);
      }
    });

    if (!headerHtml || !headerHtml.trim()) {
      return;
    }

    try {
      const parser = new DOMParser();
      const parsed = parser.parseFromString(
        `<head>${headerHtml}</head>`,
        "text/html"
      );

      parsed.head.childNodes.forEach((node) => {
        let imported = document.importNode(node, true);

        if (imported instanceof HTMLScriptElement) {
          imported = cloneExecutableScript(imported);
        }

        if (imported instanceof HTMLElement) {
          imported.setAttribute("data-portal-global-head", "true");
        }

        document.head.appendChild(imported);
      });
    } catch {
      // Ignore malformed HTML fragments from L2
    }
  }, [definition?.global?.htmlHeader]);

  // Inject arbitrary footer HTML from L2 shell (before </body>)
  useEffect(() => {
    const footerHtml = definition?.global?.htmlFooter;
    const existing = Array.from(
      document.body.querySelectorAll('[data-portal-global-footer="true"]')
    );

    existing.forEach((node) => {
      if (node.parentNode) {
        node.parentNode.removeChild(node);
      }
    });

    if (!footerHtml || !footerHtml.trim()) {
      return;
    }

    try {
      const parser = new DOMParser();
      const parsed = parser.parseFromString(
        `<body>${footerHtml}</body>`,
        "text/html"
      );

      parsed.body.childNodes.forEach((node) => {
        let imported = document.importNode(node, true);

        if (imported instanceof HTMLScriptElement) {
          imported = cloneExecutableScript(imported);
        }

        if (imported instanceof HTMLElement) {
          imported.setAttribute("data-portal-global-footer", "true");
        }

        document.body.appendChild(imported);
      });
    } catch {
      // Ignore malformed HTML fragments from L2
    }
  }, [definition?.global?.htmlFooter]);

  // Inject and execute global JS from L2 shell
  useEffect(() => {
    const jsCode = definition?.global?.js;
    const scriptId = "portal-global-js";

    const existing = document.getElementById(scriptId);
    if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
    }

    if (!jsCode || !jsCode.trim()) {
      return;
    }

    // Run shell JS in an isolated function scope so repeated injections
    // do not redeclare top-level identifiers on the window lexical scope.
    const scopedJsCode = `(function(){\n${jsCode}\n})();`;

    // Validate syntax first so malformed custom JS does not crash render.
    try {
      // eslint-disable-next-line no-new-func
      new Function(scopedJsCode);
    } catch (error) {
      console.error("Invalid global JS from shell settings", error);
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.type = "text/javascript";
    script.text = scopedJsCode;

    try {
      document.body.appendChild(script);
    } catch (error) {
      console.error("Failed to append global JS script", error);
      return;
    }

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [definition?.global?.js]);

  const themeVariables = useMemo(() => {
    if (!definition) return {};
    const theme = { ...defaultTheme, ...definition.theme };

    return {
      "--brand-font": theme.fontFamily,
      "--app-primary": theme.primary,
      "--app-accent": theme.accent,
      "--app-neutral": theme.neutral,
      "--text-primary": theme.textPrimary,
      "--text-secondary": theme.textSecondary,
      "--nav-text-color": theme.navTextColor || theme.textSecondary,
      "--surface-base": theme.surfaceBase,
      "--surface-card": theme.surfaceCard,
      "--border-subtle": theme.border,
      "--hero-gradient": theme.heroGradient,
      "--shadow-card": "0 25px 60px rgba(15, 59, 112, 0.12)",
      "--footer-bg": theme.footerBackground,
      "--footer-text": theme.footerText,
    } as React.CSSProperties;
  }, [definition]);

  const loadPageByUrl = async (targetPageUrl: string) => {
    const domain = getDomainFromQuery();

    setIsLoading(true);
    try {
      const pageResult = await fetchPageLayoutForUrl(domain, targetPageUrl);

      setDefinition((prev) => {
        if (!prev) return prev;

        const next: WebsiteDefinition = {
          ...prev,
          pageRequiresAuth: pageResult.pageRequiresAuth,
          pageAuthMessage: pageResult.pageAuthMessage,
          seo: {
            ...(prev.seo || {}),
            pageTitle: pageResult.pageTitle || "",
            pageDescription: pageResult.pageDescription || "",
            pageMetaTags: pageResult.pageMetaTags || [],
            pageImage: pageResult.pageImage || "",
            title:
              pageResult.pageTitle ||
              prev.seo?.websiteTitle ||
              prev.seo?.title,
            description:
              pageResult.pageDescription ||
              prev.seo?.websiteDescription ||
              prev.seo?.description,
            metaTags:
              pageResult.pageMetaTags && pageResult.pageMetaTags.length
                ? pageResult.pageMetaTags
                : prev.seo?.websiteMetaTags || prev.seo?.metaTags || [],
            image:
              pageResult.pageImage ||
              prev.seo?.websiteImage ||
              prev.seo?.image,
          },
          pages: {
            ...prev.pages,
            dashboard: {
              ...prev.pages.dashboard,
              hero: {
                ...prev.pages.dashboard.hero,
                heading:
                  pageResult.pageTitle || prev.pages.dashboard.hero.heading,
                description:
                  pageResult.pageDescription || prev.pages.dashboard.hero.description,
              },
              layoutRows: pageResult.layoutRows,
            },
          },
        };

        return next;
      });
      syncPageInPreviewUrl(targetPageUrl);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error && !err.message.startsWith("Failed to ")
          ? err.message
          : "Website not reachable."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn || contactProfileCompleted !== false) {
      setContactOnboardingFormId(null);
      setIsContactOnboardingFormLoading(false);
      return;
    }

    let isActive = true;

    const loadContactOnboardingForm = async () => {
      setIsContactOnboardingFormLoading(true);

      try {
        const domain = getDomainFromQuery();
        const pageResult = await fetchPageLayoutForUrl(domain, "contact-onboarding");
        if (!isActive) {
          return;
        }

        setContactOnboardingFormId(extractFirstFormIdFromRows(pageResult.layoutRows || []));
      } catch {
        if (!isActive) {
          return;
        }

        setContactOnboardingFormId(null);
      } finally {
        if (isActive) {
          setIsContactOnboardingFormLoading(false);
        }
      }
    };

    void loadContactOnboardingForm();

    return () => {
      isActive = false;
    };
  }, [isLoggedIn, contactProfileCompleted, location.search]);

  const renderCurrentPage = () => {
    if (!definition) return null;

    if (definition.pageRequiresAuth && !isLoggedIn) {
      return (
        <AuthRequiredView
          message={definition.pageAuthMessage}
          signUpUrl={definition.signUpUrl}
          loginUrl={definition.loginUrl}
          welcomeNote={definition.authContent?.welcomeNote}
          description={definition.authContent?.description}
          logoUrl={definition.authContent?.logoUrl}
        />
      );
    }

    if (isLoading) {
      return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '50px' }}>
        <img src={AppLoader} alt="Loading website definition" width={130} />
      </div>
    }

    const page = definition.pages.dashboard;
    const rows = page.layoutRows || [];

    if (!rows.length) {
      return <EmptyPageView />;
    }

    const isPageAuthRestricted = !!definition.pageRequiresAuth;

    return (
      <PageRowsWrapper>
        {rows.map((row) => {
          const rowSettings = row.settings || {};
          const backgroundImageUrl =
            rowSettings.mediaType === "Image"
              ? rowSettings.imageSignedUrl || rowSettings.imageUrl
              : undefined;

          const overlayOpacityRaw = rowSettings.imageOverlayOpacity;
          let overlayAlpha: number | undefined;
          if (typeof overlayOpacityRaw === "string" && overlayOpacityRaw.length > 0) {
            const numeric = overlayOpacityRaw.endsWith("%")
              ? parseFloat(overlayOpacityRaw.replace("%", "")) / 100
              : parseFloat(overlayOpacityRaw);
            if (!Number.isNaN(numeric)) {
              overlayAlpha = Math.max(0, Math.min(1, numeric));
            }
          }

          // Determine layout type: single-column (1) or multi-column (2)
          const isMultiColumn = row.column === 2;

          let layoutContent: React.ReactNode = null;

          if (isMultiColumn) {
            // Multi-column layout: split elements by belongsTo
            const leftElements = row.elements.filter((el) => normalizeBelongsTo(el.belongsTo) === 1);
            const rightElements = row.elements.filter((el) => normalizeBelongsTo(el.belongsTo) === 2);

            layoutContent = (
              <TwoColumns>
                <LayoutColumn>
                  {leftElements.length > 0 ? (
                    leftElements.map((element) => (
                      <ElementRenderer
                        isMultiColumn={true}
                        key={element.id}
                        element={element}
                        isPageAuthRestricted={isPageAuthRestricted}
                      />
                    ))
                  ) : (
                    <div></div>
                  )}
                </LayoutColumn>
                <LayoutColumn>
                  {rightElements.length > 0 ? (
                    rightElements.map((element) => (
                      <ElementRenderer
                        key={element.id}
                        element={element}
                        isPageAuthRestricted={isPageAuthRestricted}
                      />
                    ))
                  ) : (
                    <div></div>
                  )}
                </LayoutColumn>
              </TwoColumns>
            );
          } else {
            // Single-column layout: render all elements in one column
            layoutContent = (
              <SingleColumn>
                {row.elements.length > 0 ? (
                  row.elements.map((element) => (
                    <ElementRenderer
                      key={element.id}
                      element={element}
                      isPageAuthRestricted={isPageAuthRestricted}
                    />
                  ))
                ) : (
                  <div></div>
                )}
              </SingleColumn>
            );
          }

          return (
            <LayoutRowShell
              key={row.id}
              $bgColor={rowSettings.bgColor}
              $backgroundImageUrl={backgroundImageUrl}
              $overlayColor={rowSettings.imageOverlayColor}
              $overlayOpacity={overlayAlpha}
            >
              {layoutContent}
            </LayoutRowShell>
          );
        })}
      </PageRowsWrapper>
    );
  };

  const handleFooterLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    link: FooterLink
  ) => {
    if (!link.url) return;

    const isExternal = link.isExternal || /^https?:\/\//i.test(link.url);
    if (isExternal) {
      // Let the browser handle normal external navigation
      return;
    }

    event.preventDefault();

    const matchingNavItem = definition?.navigation.items.find((navItem) =>
      navItem.pageUrl === link.url || navItem.label === link.label
    );

    if (matchingNavItem) {
      setActiveNavLabel(matchingNavItem.label);
      if (matchingNavItem.slug) {
        setActivePage(matchingNavItem.slug);
      }
    }

    void loadPageByUrl(link.url);
  };

  const handleUtilityLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    link: UtilityLink
  ) => {
    if (!link.url) return;

    const isExternal = link.isExternal || /^https?:\/\//i.test(link.url);
    if (isExternal) {
      return;
    }

    event.preventDefault();

    const matchingNavItem = definition?.navigation.items.find((navItem) =>
      navItem.pageUrl === link.url || navItem.label === link.label
    );

    if (matchingNavItem) {
      setActiveNavLabel(matchingNavItem.label);
      if (matchingNavItem.slug) {
        setActivePage(matchingNavItem.slug);
      }
    }

    void loadPageByUrl(link.url);
  };

  const handleNavClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    item: NavigationItem
  ) => {
    // Allow normal navigation for items without an associated page URL/slug
    if (!item.slug && !item.pageUrl) {
      return;
    }

    const isExternal =
      Boolean(item.href) ||
      (item.pageUrl ? /^https?:\/\//i.test(item.pageUrl) : false);

    if (isExternal) {
      // Respect browser navigation behavior for external URLs.
      return;
    }

    event.preventDefault();

    // Track which nav item is currently active for styling
    setActiveNavLabel(item.label);

    if (item.slug) {
      setActivePage(item.slug);
    }

    const targetPageUrl =
      item.pageUrl ||
      (item.slug ? (item.slug === "dashboard" ? "home" : item.slug) : undefined);

    if (!targetPageUrl) {
      return;
    }

    void loadPageByUrl(targetPageUrl);
  };

  const handleCookieConsentChoice = (choice: "accepted" | "rejected") => {
    console.log(`Cookie consent ${choice}`);

    try {
      const domain = getDomainFromQuery();
      const storageKey = `${COOKIE_CONSENT_STORAGE_PREFIX}_${domain}`;
      localStorage.setItem(storageKey, choice);
    } catch {
      // Ignore localStorage write failures (private mode, blocked storage, etc.)
    }

    setShowCookieConsent(false);
  };

  const renderCookieConsentBanner = () => {
    if (!showCookieConsent) {
      return null;
    }

    return (
      <CookieConsentWrapper>
        <CookieConsentCard>
          <CookieCardInner>
            <CookieConsentHeader>
              <CookieIcon size={20} color="#D68600" />
              <CookieConsentTitle>We use cookies</CookieConsentTitle>
            </CookieConsentHeader>

            <CookieContentPad>
              <CookieConsentDescription>
                We use cookies to personalize your site experience and analyze the site traffic. By clicking
                Accept All Cookies, you agree to the storing of cookies on your device.
              </CookieConsentDescription>
            </CookieContentPad>

            <CookieConsentActions>
              <CookieActionButton
                type="button"
                onClick={() => handleCookieConsentChoice("rejected")}
              >
                Reject All
              </CookieActionButton>
              <CookieActionButtonPrimary
                type="button"
                onClick={() => handleCookieConsentChoice("accepted")}
              >
                Accept All Cookies
              </CookieActionButtonPrimary>
            </CookieConsentActions>
          </CookieCardInner>
        </CookieConsentCard>
      </CookieConsentWrapper>
    );
  };

  if (isLoggedIn && contactProfileCompleted === false) {
    const canRenderContactOnboardingForm =
      typeof auth?.userId === "number" &&
      Number.isFinite(auth.userId) &&
      !!contactOnboardingFormId;

    return (
      <>
        <ContactOnboardingHeaderBar>
          <ContactOnboardingHeaderLeft>
            {definition?.brand.logoUrl && (
              <ContactOnboardingHeaderLogoImage
                src={definition.brand.logoUrl}
                alt={definition.brand.name || "Logo"}
              />
            )}
          </ContactOnboardingHeaderLeft>

          <ContactOnboardingHeaderRight>
            <ContactOnboardingProfileTriggerButton
              type="button"
              onClick={() => setIsProfileOpen((prev) => !prev)}
            >
              <ContactOnboardingProfileAvatarCircle>
                {profileImageUrl ? (
                  <ContactOnboardingProfileAvatarImage
                    src={profileImageUrl}
                    alt={profileDisplayName || "Profile"}
                  />
                ) : (
                  (profileDisplayName || profileEmail || "User").charAt(0).toUpperCase()
                )}
              </ContactOnboardingProfileAvatarCircle>
              <ContactOnboardingProfileTriggerText>
                {profileDisplayName || "Member"}
              </ContactOnboardingProfileTriggerText>
              <ChevronDownIcon color="#6b7280" />
            </ContactOnboardingProfileTriggerButton>

            {isProfileOpen && (
              <ContactOnboardingDropdownWrapper>
                <ProfileDropdown
                  name={profileDisplayName || undefined}
                  email={profileEmail || undefined}
                  imageUrl={profileImageUrl}
                  showMenuItems={false}
                />
              </ContactOnboardingDropdownWrapper>
            )}
          </ContactOnboardingHeaderRight>
        </ContactOnboardingHeaderBar>

        <OnboardingAnnouncementBar
          value={profileCompletionPercentage ?? 0}
          onClick={() => {
            // Already on required onboarding step.
          }}
        />

        <ContactOnboardingGate>
          <ContactOnboardingIntro>
            <ContactOnboardingIntroTitle>Complete onboarding to continue</ContactOnboardingIntroTitle>
            <ContactOnboardingIntroSubtitle>
              You must fill out this information before proceed further
            </ContactOnboardingIntroSubtitle>
          </ContactOnboardingIntro>

          <ContactOnboardingCard>
            {isContactOnboardingFormLoading ? (
              <StateBlock>Loading contact onboarding form…</StateBlock>
            ) : canRenderContactOnboardingForm ? (
              <React.Suspense fallback={<StateBlock>Loading contact onboarding form…</StateBlock>}>
                <LazyFormFiller
                  onClose={() => { }}
                  formType="contact"
                  entityId={auth.userId}
                  inlineMode
                  allowFormSwitch={false}
                  hidePagesTitles
                  hideFormTitle
                  includeMetadata={false}
                  hideOrganizationsSection
                  hideCloseButton
                  editFormId={contactOnboardingFormId}
                />
              </React.Suspense>
            ) : (
              <StateBlock>Contact onboarding form is not configured.</StateBlock>
            )}
          </ContactOnboardingCard>
        </ContactOnboardingGate>
        {renderCookieConsentBanner()}
      </>
    );
  }

  if (isLoggedIn && (hasPrimaryOrganization === false || forceShowNoPrimaryOrganizationView)) {
    return (
      <>
        <NoPrimaryOrganizationView
          fullName={profileDisplayName}
          email={profileEmail}
          profileImageUrl={profileImageUrl}
          logoUrl={definition?.brand.logoUrl}
          profileCompletionPercentage={profileCompletionPercentage ?? 0}
          contactProfileCompleted={contactProfileCompleted}
          onSkipToDashboard={() => {
            setForceShowNoPrimaryOrganizationView(false);
            setHasPrimaryOrganization(true);
          }}
          onCompleteProfile={() => {
            setForceShowNoPrimaryOrganizationView(false);
            setHasPrimaryOrganization(true);
            void loadPageByUrl("contact-onboarding");
          }}
          onEnterOnboardingIntro={() => {
            // Organization now exists; keep onboarding flow but leave no-org gate.
            setForceShowNoPrimaryOrganizationView(false);
            setHasPrimaryOrganization(true);
          }}
        />
        {renderCookieConsentBanner()}
      </>
    );
  }

  if (isLoading && !definition) {
    return (
      <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <img src={AppLoader} alt="Loading website definition" width={130} />
      </div>
    );
  }

  if (!definition) {
    return <StateBlock>{error ?? "No website configuration found."}</StateBlock>;
  }

  // Show all navigation, utility, and footer links to everyone.
  // Access control is handled when loading the target page, where
  // we either show the login/join portal view or a no-permission view.

  const visibleNavigationItems: NavigationItem[] = definition.navigation.items;

  const visibleUtilityLinks: UtilityLink[] = definition.utilityLinks;

  // Footer links: cap at 3 columns.
  // For small totals, spread links so we have at least 2 per column
  // (e.g. 6 links -> 2 per column across 3 columns).
  // If more than 15 links overall, allow up to 6 per column to stay
  // within the 3-column cap.
  const totalFooterLinks = definition.footer.columns.reduce(
    (sum, column) => sum + column.items.length,
    0
  );

  const footerItemsPerColumn = totalFooterLinks > 15
    ? 6
    : Math.max(2, Math.ceil(totalFooterLinks / 3));

  let renderedFooterColumns = 0;

  const footerColumns: React.ReactElement[] = [];

  definition.footer.columns.forEach((column, columnIndex) => {
    if (renderedFooterColumns >= 3) {
      return;
    }

    const footerItems = column.items;

    if (footerItems.length === 0) {
      return;
    }

    const chunks: FooterLink[][] = [];
    for (let i = 0; i < footerItems.length; i += footerItemsPerColumn) {
      chunks.push(footerItems.slice(i, i + footerItemsPerColumn));
    }

    chunks.forEach((itemsChunk, chunkIndex) => {
      if (renderedFooterColumns >= 3) {
        return;
      }

      renderedFooterColumns += 1;

      footerColumns.push(
        <FooterColumn
          key={`${column.heading || `column-${columnIndex}`}-${chunkIndex}`}
        >
          {chunkIndex === 0 && column.heading && (
            <FooterColumnHeading>{column.heading}</FooterColumnHeading>
          )}
          {itemsChunk.map((item, itemIndex) => {
            const isExternal =
              item.isExternal || (item.url ? /^https?:\/\//i.test(item.url) : false);
            const shouldOpenNewTab = item.openNewTab === true;
            const hasUrl = !!item.url;

            return (
              <FooterColumnItem key={`${item.label}-${itemIndex}`}>
                {hasUrl ? (
                  <a
                    href={item.url}
                    onClick={(event) => {
                      if (!isExternal) {
                        handleFooterLinkClick(event, item);
                      }
                    }}
                    target={shouldOpenNewTab ? "_blank" : undefined}
                    rel={shouldOpenNewTab ? "noreferrer" : undefined}
                  >
                    {item.label}
                  </a>
                ) : (
                  item.label
                )}
              </FooterColumnItem>
            );
          })}
        </FooterColumn>
      );
    });
  });

  const internalAppPages = ["organizationdetails", "contactdetails", "settings"];
  const isOnInternalPage = internalAppPages.includes(queryPage);
  const privacyPolicyUrl = normalizeExternalUrl(definition.footer.legal?.privacyPolicy);
  const termsOfServiceUrl = normalizeExternalUrl(definition.footer.legal?.termsOfService);

  return (
    <>
      <WebsiteShell style={themeVariables}>
        {auth?.viewerName &&
          <ForceLoginBar />
        }
        <TopUtilityBar>
          {/* <UtilityBrand>{definition.brand.name}</UtilityBrand> */}
          <UtilityLinks>
            {visibleUtilityLinks.map((link, index) => {
              const shouldOpenNewTab = link.openNewTab === true;

              return (
                <a
                  key={`${link.label}-${link.url || ""}-${index}`}
                  href={link.url || "#utility"}
                  onClick={(event) => {
                    if (!link.isExternal) {
                      handleUtilityLinkClick(event, link);
                    }
                  }}
                  target={shouldOpenNewTab ? "_blank" : undefined}
                  rel={shouldOpenNewTab ? "noreferrer" : undefined}
                >
                  {link.label}
                </a>
              )
            })}
          </UtilityLinks>
        </TopUtilityBar>

        {isLoggedIn &&
          contactProfileCompleted === false &&
          queryPage !== "contact-onboarding" && (
            <OnboardingAnnouncementBar
              value={profileCompletionPercentage ?? 0}
              onClick={() => {
                void loadPageByUrl("contact-onboarding");
              }}
            />
          )}

        <NavigationBar
          $isCenterStacked={definition.navigation.headerLayout === "center-stacked"}
        >
          {definition.navigation.headerLayout === "center-stacked" ? (
            <>
              {/* Absolutely centered logo + nav */}
              <NavCenterStack>
                <BrandBlock>
                  {definition.brand.logoUrl && (
                    <BrandIcon>
                      <BrandLogo
                        src={definition.brand.logoUrl}
                        alt={definition.brand.name || "Brand logo"}
                      />
                    </BrandIcon>
                  )}
                </BrandBlock>
                <NavItems $layout="center-stacked">
                  {visibleNavigationItems.map((item) => (
                    <NavItemWrapper key={item.label} onMouseEnter={() => setHoveredNav(item.label)} onMouseLeave={() => setHoveredNav((c) => (c === item.label ? null : c))}>
                      <NavLink
                        $isActive={!isOnInternalPage && (activeNavLabel ? activeNavLabel === item.label : item.slug === activePage)}
                        onClick={(e) => handleNavClick(e, item)}
                        href={item.href ?? "#"}
                        target={item.openNewTab ? "_blank" : undefined}
                        rel={item.openNewTab ? "noreferrer" : undefined}
                      >
                        {item.label}
                      </NavLink>
                      {item.subItems && hoveredNav === item.label && (
                        <DropdownPanel>
                          {item.subItems.map((sub) => (<DropdownItem key={sub.label} href={sub.href || "#"} target={sub.openNewTab ? "_blank" : undefined} rel={sub.openNewTab ? "noreferrer" : undefined} onClick={(e) => handleNavClick(e, { label: sub.label, pageUrl: sub.pageUrl, href: sub.href, openNewTab: sub.openNewTab })}>{sub.label}</DropdownItem>))}
                        </DropdownPanel>
                      )}
                    </NavItemWrapper>
                  ))}
                </NavItems>
              </NavCenterStack>
              {/* NavActions always far right */}
              <NavActions ref={navActionsRef} style={{ marginLeft: "auto" }}>
                <IconButton aria-label="Search"><SearchIcon size={18} /></IconButton>
                <IconButton aria-label="Notifications" onClick={() => { setIsNotificationsOpen((o) => !o); setIsProfileOpen(false); }}><NotificationIcon size={18} /></IconButton>
                <NavDivider />
                <ProfileTrigger
                  onClick={() => {
                    if (isLoggedIn) {
                      setIsProfileOpen((o) => !o);
                      setIsNotificationsOpen(false);
                    } else if (definition.loginUrl) {
                      window.location.href = definition.loginUrl;
                    }
                  }}
                >
                  <ProfileInitialsCircle>
                    {isLoggedIn ? (
                      profileImageUrl ? (
                        <ProfileAvatarImage
                          src={profileImageUrl}
                          alt={profileDisplayName || "Profile picture"}
                        />
                      ) : (
                        profileInitials
                      )
                    ) : (
                      <LoginUserIcon size={18} color="var(--text-secondary)" />
                    )}
                  </ProfileInitialsCircle>
                  <ProfileTextStack>
                    <ProfileTriggerName>{profileDisplayName}</ProfileTriggerName>
                  </ProfileTextStack>
                  {isLoggedIn && (
                    <ProfileChevron>
                      <ChevronDownIcon color="var(--text-secondary)" />
                    </ProfileChevron>
                  )}
                </ProfileTrigger>
                {isLoggedIn && isProfileOpen && (
                  <ProfileDropdown
                    name={profileDisplayName}
                    email={profileEmail}
                    imageUrl={profileImageUrl}
                    userId={auth?.userId!}
                    organizationId={auth?.organizationId!}
                    onMenuItemClick={() => setIsProfileOpen(false)}
                  />
                )}
              </NavActions>
            </>
          ) : definition.navigation.headerLayout === "right-left" ? (
            <>
              {/* Menu far left, logo right of menu, NavActions always far right */}
              <NavItems $layout="right-left">
                {visibleNavigationItems.map((item) => (
                  <NavItemWrapper key={item.label} onMouseEnter={() => setHoveredNav(item.label)} onMouseLeave={() => setHoveredNav((c) => (c === item.label ? null : c))}>
                    <NavLink
                      $isActive={!isOnInternalPage && (activeNavLabel ? activeNavLabel === item.label : item.slug === activePage)}
                      onClick={(e) => handleNavClick(e, item)}
                      href={item.href ?? "#"}
                      target={item.openNewTab ? "_blank" : undefined}
                      rel={item.openNewTab ? "noreferrer" : undefined}
                    >
                      {item.label}
                    </NavLink>
                    {item.subItems && hoveredNav === item.label && (
                      <DropdownPanel>
                        {item.subItems.map((sub) => (<DropdownItem key={sub.label} href={sub.href || "#"} target={sub.openNewTab ? "_blank" : undefined} rel={sub.openNewTab ? "noreferrer" : undefined} onClick={(e) => handleNavClick(e, { label: sub.label, pageUrl: sub.pageUrl, href: sub.href, openNewTab: sub.openNewTab })}>{sub.label}</DropdownItem>))}
                      </DropdownPanel>
                    )}
                  </NavItemWrapper>
                ))}
              </NavItems>
              <BrandBlock style={{ marginLeft: "auto" }}>
                {definition.brand.logoUrl && (
                  <BrandIcon>
                    <BrandLogo
                      src={definition.brand.logoUrl}
                      alt={definition.brand.name || "Brand logo"}
                    />
                  </BrandIcon>
                )}
              </BrandBlock>
              <NavActions ref={navActionsRef}>
                <IconButton aria-label="Search"><SearchIcon size={18} /></IconButton>
                <IconButton aria-label="Notifications" onClick={() => { setIsNotificationsOpen((o) => !o); setIsProfileOpen(false); }}><NotificationIcon size={18} /></IconButton>
                <NavDivider />
                <ProfileTrigger
                  onClick={() => {
                    if (isLoggedIn) {
                      setIsProfileOpen((o) => !o);
                      setIsNotificationsOpen(false);
                    } else if (definition.loginUrl) {
                      window.location.href = definition.loginUrl;
                    }
                  }}
                >
                  <ProfileInitialsCircle>
                    {isLoggedIn ? (
                      profileImageUrl ? (
                        <ProfileAvatarImage
                          src={profileImageUrl}
                          alt={profileDisplayName || "Profile picture"}
                        />
                      ) : (
                        profileInitials
                      )
                    ) : (
                      <LoginUserIcon size={18} color="var(--text-secondary)" />
                    )}
                  </ProfileInitialsCircle>
                  <ProfileTextStack>
                    <ProfileTriggerName>{profileDisplayName}</ProfileTriggerName>
                  </ProfileTextStack>
                  {isLoggedIn && (
                    <ProfileChevron>
                      <ChevronDownIcon color="var(--text-secondary)" />
                    </ProfileChevron>
                  )}
                </ProfileTrigger>
                {isLoggedIn && isProfileOpen && (
                  <ProfileDropdown
                    name={profileDisplayName}
                    email={profileEmail}
                    imageUrl={profileImageUrl}
                    userId={auth?.userId!}
                    organizationId={auth?.organizationId!}
                    onMenuItemClick={() => setIsProfileOpen(false)}
                  />
                )}
              </NavActions>
            </>
          ) : (
            /* left-right (default): logo far left, menu + actions far right */
            <>
              <BrandBlock>
                {definition.brand.logoUrl && (
                  <BrandIcon>
                    <BrandLogo
                      src={definition.brand.logoUrl}
                      alt={definition.brand.name || "Brand logo"}
                    />
                  </BrandIcon>
                )}
              </BrandBlock>
              <NavGroup style={{ marginLeft: "auto" }}>
                <NavItems $layout="left-right">
                  {visibleNavigationItems.map((item) => (
                    <NavItemWrapper key={item.label} onMouseEnter={() => setHoveredNav(item.label)} onMouseLeave={() => setHoveredNav((c) => (c === item.label ? null : c))}>
                      <NavLink
                        $isActive={!isOnInternalPage && (activeNavLabel ? activeNavLabel === item.label : item.slug === activePage)}
                        onClick={(e) => handleNavClick(e, item)}
                        href={item.href ?? "#"}
                        target={item.openNewTab ? "_blank" : undefined}
                        rel={item.openNewTab ? "noreferrer" : undefined}
                      >
                        {item.label}
                      </NavLink>
                      {item.subItems && hoveredNav === item.label && (
                        <DropdownPanel>
                          {item.subItems.map((sub) => (<DropdownItem key={sub.label} href={sub.href || "#"} target={sub.openNewTab ? "_blank" : undefined} rel={sub.openNewTab ? "noreferrer" : undefined} onClick={(e) => handleNavClick(e, { label: sub.label, pageUrl: sub.pageUrl, href: sub.href, openNewTab: sub.openNewTab })}>{sub.label}</DropdownItem>))}
                        </DropdownPanel>
                      )}
                    </NavItemWrapper>
                  ))}
                </NavItems>
                <NavActions ref={navActionsRef}>
                  <IconButton aria-label="Search"><SearchIcon size={18} /></IconButton>
                  <IconButton aria-label="Notifications" onClick={() => { setIsNotificationsOpen((o) => !o); setIsProfileOpen(false); }}><NotificationIcon size={18} /></IconButton>
                  <NavDivider />
                  <ProfileTrigger
                    onClick={() => {
                      if (isLoggedIn) {
                        setIsProfileOpen((o) => !o);
                        setIsNotificationsOpen(false);
                      } else if (definition.loginUrl) {
                        window.location.href = definition.loginUrl;
                      }
                    }}
                  >
                    <ProfileInitialsCircle>
                      {isLoggedIn ? (
                        profileImageUrl ? (
                          <ProfileAvatarImage
                            src={profileImageUrl}
                            alt={profileDisplayName || "Profile picture"}
                          />
                        ) : (
                          profileInitials
                        )
                      ) : (
                        <LoginUserIcon size={18} color="var(--text-secondary)" />
                      )}
                    </ProfileInitialsCircle>
                    <ProfileTextStack>
                      <ProfileTriggerName>{profileDisplayName}</ProfileTriggerName>
                    </ProfileTextStack>
                    {isLoggedIn && (
                      <ProfileChevron>
                        <ChevronDownIcon color="var(--text-secondary)" />
                      </ProfileChevron>
                    )}
                  </ProfileTrigger>
                  {isLoggedIn && isProfileOpen && (
                    <ProfileDropdown
                      name={profileDisplayName}
                      email={profileEmail}
                      imageUrl={profileImageUrl}
                      userId={auth?.userId!}
                      organizationId={auth?.organizationId!}
                      onMenuItemClick={() => setIsProfileOpen(false)}
                    />
                  )}
                </NavActions>
              </NavGroup>
            </>
          )}
        </NavigationBar>

        {/* {isNotificationsOpen && <NotificationsDropdown />} */}

        {queryPage === "organizationdetails" ? (
          <OrganizationDetails />
        ) : queryPage === "contactdetails" ? (
          <ContactDetails />
        ) : queryPage === "settings" ? (
          <AccountSettings />
        ) : (
          renderCurrentPage()
        )}

        <Footer>
          <FooterTopRow>
            <FooterLeftBlock>
              <FooterBrandBlock>
                {definition.footer.logoUrl && (
                  <FooterLogo
                    src={definition.footer.logoUrl}
                    alt={definition.brand.name || "Footer logo"}
                  />
                )}
                {/* {definition.brand.name && (
                      <FooterBrandName>{definition.brand.name}</FooterBrandName>
                    )} */}
              </FooterBrandBlock>
              <FooterTextBlock>
                <FooterSummary>{definition.footer.summary}</FooterSummary>
              </FooterTextBlock>
              {definition.footer.socialLinks &&
                definition.footer.socialLinks.length > 0 && (
                  <FooterSocialRow>
                    {[...definition.footer.socialLinks]
                      .sort((a, b) => {
                        const aPlatform = a.platform.toLowerCase();
                        const bPlatform = b.platform.toLowerCase();
                        const aIndex = FOOTER_SOCIAL_PLATFORM_ORDER.indexOf(aPlatform);
                        const bIndex = FOOTER_SOCIAL_PLATFORM_ORDER.indexOf(bPlatform);
                        const aOrder = aIndex === -1 ? FOOTER_SOCIAL_PLATFORM_ORDER.length : aIndex;
                        const bOrder = bIndex === -1 ? FOOTER_SOCIAL_PLATFORM_ORDER.length : bIndex;
                        return aOrder - bOrder;
                      })
                      .map((link) => {
                        const platform = link.platform.toLowerCase();
                        return (
                          <FooterSocialIcon
                            key={link.url}
                            href={link.url}
                            target="_blank"
                            rel="noreferrer"
                            data-platform={platform}
                          >
                            {platform === "facebook" && <FacebookIcon size={22} />}
                            {platform === "linkedin" && <LinkedInIcon size={22} />}
                            {platform === "youtube" && <YoutubeIcon size={22} />}
                            {platform === "twitter" && <NewXIcon size={22} />}
                          </FooterSocialIcon>
                        );
                      })}
                  </FooterSocialRow>
                )}
            </FooterLeftBlock>
            <FooterLinksRow>
              <FooterColumnsWrapper>
                {footerColumns}
              </FooterColumnsWrapper>
            </FooterLinksRow>
            {definition.footer.address && (
              <FooterAddress>{definition.footer.address}</FooterAddress>
            )}
          </FooterTopRow>
          <FooterLegal>
            <span>
              {definition.footer.copyrightText ||
                "Copyright © 2026. All Rights Reserved."}
            </span>
            <FooterLinks>
              {definition.footer.showPrivacyPolicy !== false &&
                privacyPolicyUrl && (
                  <a href={privacyPolicyUrl} target="_blank" rel="noreferrer noopener">
                    Privacy Policy
                  </a>
                )}
              {definition.footer.showTermsAndConditions !== false &&
                termsOfServiceUrl && (
                  <a href={termsOfServiceUrl} target="_blank" rel="noreferrer noopener">
                    Terms & Conditions
                  </a>
                )}
            </FooterLinks>
          </FooterLegal>
        </Footer>
      </WebsiteShell>
      {renderCookieConsentBanner()}
    </>
  );
};

const AuthRequiredView = ({
  message,
  signUpUrl,
  loginUrl,
  welcomeNote,
  description,
  logoUrl,
}: {
  message?: string;
  signUpUrl?: string;
  loginUrl?: string;
  welcomeNote?: string;
  description?: string;
  logoUrl?: string;
}) => {
  return (
    <AuthBackdrop>
      <AuthCard>
        <AuthBeamTopLeft />
        <AuthBeamTopRight />
        <AuthBeamBottomLeft />
        <AuthBeamBottomRight />

        <AuthInner>
          <AuthIllustrationCircle
            src={logoUrl || AuthPortalIllustration}
            alt="Member portal illustration"
          />

          <AuthTitle>
            {welcomeNote || (
              <>
                Welcome To <span style={{ color: "#FF6D41" }}>Member things </span>L3 Portal
              </>
            )}
          </AuthTitle>
          <AuthSubtitle>
            {description || "Log into your existing account and upgrade your demographic information."}
          </AuthSubtitle>

          {message && <AuthMessage>{message}</AuthMessage>}

          <AuthButtons>
            {signUpUrl && (
              <GhostButton as="a" href={signUpUrl}>
                Join Portal
              </GhostButton>
            )}
            {loginUrl ? (
              <PrimaryButton as="a" href={loginUrl}>
                Login / Update Profile
              </PrimaryButton>
            ) : null}
          </AuthButtons>


          <AuthInfoCard>
            <AuthInfoTitle>Profile Updates</AuthInfoTitle>
            <AuthInfoList>
              <li>Log in and click your name in the upper-right corner.</li>
              <li>Select the Account setting from profile dropdown.</li>
              <li>Update your demographic information and save.</li>
            </AuthInfoList>
          </AuthInfoCard>
        </AuthInner>
      </AuthCard>
    </AuthBackdrop>
  );
};

const NoPermissionView = ({ message }: { message?: string }) => {
  return (
    <AuthBackdrop>
      <AuthCard>
        <AuthInner>
          <AuthTitle>Access to this page is restricted</AuthTitle>
          <AuthSubtitle>
            {message || "You do not have permission to access this page."}
          </AuthSubtitle>
        </AuthInner>
      </AuthCard>
    </AuthBackdrop>
  );
};

const EmptyPageView = () => {
  return (
    <AuthBackdrop>
      <AuthCard>
        <AuthInner>
          <AuthTitle>Nothing to show on this page</AuthTitle>
          <AuthSubtitle>
            This page does not have any content configured yet.
          </AuthSubtitle>
        </AuthInner>
      </AuthCard>
    </AuthBackdrop>
  );
};

const ElementRenderer = ({
  isMultiColumn,
  element,
  isPageAuthRestricted,
}: {
  isMultiColumn?: boolean;
  element: PageElement;
  isPageAuthRestricted?: boolean;
}) => {
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const auth = useSelector(selectAuth) as ReturnType<typeof selectAuth>;
  const config = element.config || {};

  const htmlConfig = config.htmlConfig || {};
  const attrs = (htmlConfig.attributes || {}) as {
    class?: string;
    href?: string;
    role?: string;
    target?: string;
  };
  const className = config.elementClass || attrs.class;

  const searchParams = new URLSearchParams(window.location.search);
  const domainFromQuery = searchParams.get("domain");
  const elementDomain = domainFromQuery ? decodeURIComponent(domainFromQuery) : window.location.origin;

  let content: React.ReactNode = null;

  const authRequiredAccessKeys: string[] = [
    "public_directory",
    "form",
    "contact_summary",
    "organization_summary",
  ];

  const requiresAuth = authRequiredAccessKeys.includes(element.accessKey);
  const isLockedForAnonUser = requiresAuth && !isLoggedIn && !isPageAuthRestricted;

  if (isLockedForAnonUser) {
    content = (
      <LockedElementHint>
        <strong>Login required</strong>
        <span>
          This section is available only to logged-in users.
        </span>
      </LockedElementHint>
    );

    return <LayoutElement $isDisabled>{content}</LayoutElement>;
  }

  switch (element.accessKey) {
    case "paragraph": {
      const Tag: any = htmlConfig.tag || "p";
      const text =
        config.htmlContent ||
        config.paragraphContent ||
        config.content ||
        "";

      const hasHtml = /<[^>]+>/.test(text);

      const style: React.CSSProperties = {};
      if (config.paragraphColor) style.color = config.paragraphColor;
      if (config.paragraphFontSize) style.fontSize = config.paragraphFontSize;
      // if (config.paragraphFontWeight) style.fontWeight = config.paragraphFontWeight;
      // if (config.paragraphTextAlignment) style.textAlign = config.paragraphTextAlignment;

      content = hasHtml ? (
        <div
          className={`ql-editor ` + className}
          style={style}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      ) : (
        <Tag className={className} style={style}>
          {text}
        </Tag>
      );
      break;
    }
    case "heading": {
      const Tag: any = config.heading || htmlConfig.tag || "h2";
      const text = config.htmlContent || config.content || "";
      const style: React.CSSProperties = {
        // Default: use primary color for headings
        color: "var(--app-primary)",
      };
      if (config.color) style.color = config.color;
      if (config.fontSize) style.fontSize = config.fontSize;
      if (config.fontWeight) style.fontWeight = config.fontWeight;
      if (config.textAlignment) style.textAlign = config.textAlignment;

      content = (
        <Tag className={className} style={style}>
          {text}
        </Tag>
      );
      break;
    }
    case "button": {
      const Tag: any = htmlConfig.tag || "a";
      const text = config.content || config.htmlContent || "";
      const style: React.CSSProperties = {
        border: "none",
        padding: "12px 30px",
        borderRadius: "6px",
        fontWeight: 500,
        cursor: "pointer",
        transition: "background-color 0.2s ease",
        // Fallback: use primary color for main buttons
        backgroundColor: "var(--app-primary)",
        color: "#fff",
      };
      if (config.buttonColor) style.backgroundColor = config.buttonColor;
      if (config.buttonTextColor) style.color = config.buttonTextColor;
      style.textDecoration = "none";

      const href = config.buttonLink || attrs.href || "#";
      const target = config.openNewTab ? "_blank" : attrs.target;

      const wrapperStyle: React.CSSProperties = {};
      if (config.buttonAlignment) wrapperStyle.textAlign = config.buttonAlignment;

      content = (
        <div style={wrapperStyle}>
          <Tag
            className={className}
            href={href}
            role={attrs.role}
            target={target}
            style={style}
            onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
              if (config.buttonHoverColor) {
                (e.currentTarget as HTMLElement).style.backgroundColor =
                  config.buttonHoverColor;
              }
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
              if (config.buttonColor) {
                (e.currentTarget as HTMLElement).style.backgroundColor =
                  config.buttonColor;
              }
            }}
          >
            {text}
          </Tag>
        </div>
      );
      break;
    }
    case "form": {
      const formType = config.scope === "organization" ? "organization" : "contact";
      const formId = config.formId || config.formID;
      const currentPageUrlRaw = new URLSearchParams(window.location.search)
        .get("page");
      const currentPageUrl = (currentPageUrlRaw || "").toLowerCase();
      const isContactOnboarding = currentPageUrl === "contact-onboarding";
      const isOrganizationOnboarding =
        currentPageUrl === "organisation-onboarding" ||
        currentPageUrl === "organization-onboarding";
      const rawRecordId =
        formType === "contact" ? auth?.userId : auth?.organizationId;
      const recordId =
        typeof rawRecordId === "number" && Number.isFinite(rawRecordId)
          ? rawRecordId
          : null;

      if ((isContactOnboarding || isOrganizationOnboarding) && formId) {
        let recordId: number | null = null;

        // For contact onboarding, the record we are updating is the
        // contact returned by /portal/me, whose id is stored on auth.userId.
        if (isContactOnboarding && auth?.userId) {
          recordId = auth.userId;
        }

        // For organization onboarding, prefer the organizationId from auth.
        if (!recordId && isOrganizationOnboarding && auth?.organizationId) {
          recordId = auth.organizationId;
        }

        content = (
          <React.Suspense fallback={<div>Loading...</div>}>
            {recordId && !Number.isNaN(recordId) ? (
              <LazyFormFiller
                onClose={() => { }}
                formType={formType}
                entityId={recordId}
                inlineMode
                includeMetadata={false}
                hideFormTitle
                hidePagesTitles
                allowFormSwitch={false}
                hideOrganizationsSection
                hideCloseButton={isContactOnboarding}
                editFormId={formId}
                onSuccess={isContactOnboarding ? undefined : undefined}
              />
            ) : (
              <span>Unable to determine record to update for onboarding form.</span>
            )}
          </React.Suspense>
        );
      } else {
        content = (
          <React.Suspense fallback={<div>Loading...</div>}>
            {formId ? (
              recordId ? (
                <LazyFormFiller
                  onClose={() => { }}
                  formType={formType}
                  entityId={recordId}
                  editFormId={formId}
                  inlineMode
                  hideCloseButton
                  includeMetadata={false}
                  hideFormTitle
                  hidePagesTitles
                />
              ) : (
                <LazyFormFiller
                  onClose={() => { }}
                  formType={formType}
                  formId={formId}
                  inlineMode
                  hideCloseButton
                  includeMetadata={false}
                  hideFormTitle
                  hidePagesTitles
                />
              )
            ) : (
              <span>No form selected. Configure form in settings.</span>
            )}
          </React.Suspense>
        );
      }
      break;
    }
    case "separator": {
      const style: React.CSSProperties = {
        border: "none",
        borderTop: `${config.separatorHeight || "1px"} ${config.separatorType || "solid"} ${config.separatorColor || "var(--app-secondary-color)"}`,
        width: "100%",
        margin: "0",
      };
      if (config.alignment === "center") {
        Object.assign(style, { marginLeft: "auto", marginRight: "auto", width: "60%" });
      } else if (config.alignment === "right") {
        Object.assign(style, { marginLeft: "auto", width: "60%" });
      }
      content = <hr className={className} style={style} role="separator" />;
      break;
    }
    case "media": {
      const rawMediaAttrs = htmlConfig.attributes as unknown as { alt?: string; src?: string };
      const mediaType = (config.mediaType || "").toString().toLowerCase();
      const src = config.imageSignedUrl || config.imageUrl || config.imageExternalURL || rawMediaAttrs?.src || "";
      const alt = config.alt || rawMediaAttrs?.alt || "";
      const wrapperStyle: React.CSSProperties = { width: "100%", display: "flex" };
      if (config.alignment === "center") wrapperStyle.justifyContent = "center";
      else if (config.alignment === "right") wrapperStyle.justifyContent = "flex-end";
      else wrapperStyle.justifyContent = "flex-start";

      // Video rendering (local file or external URL)
      const videoSrc = config.videoSignedUrl || config.videoUrl || "";
      const videoExternalUrl = config.videoExternalURL || "";

      // Decide whether this element should be rendered as video.
      // Respect an explicit mediaType of "image" even if video URLs exist.
      const shouldRenderVideo =
        mediaType === "video" ||
        (!mediaType && !src && (videoSrc !== "" || videoExternalUrl !== ""));

      if (shouldRenderVideo) {
        // External video (e.g., YouTube)
        if (videoExternalUrl) {
          let embedUrl = videoExternalUrl;

          try {
            const url = new URL(videoExternalUrl);

            // Handle standard YouTube watch URLs
            if (url.hostname.includes("youtube.com") && url.pathname === "/watch") {
              const videoId = url.searchParams.get("v");
              if (videoId) {
                url.pathname = `/embed/${videoId}`;
                url.searchParams.delete("v");
                embedUrl = url.toString();
              }
            }

            // Handle youtu.be short URLs
            if (url.hostname === "youtu.be") {
              const videoId = url.pathname.replace("/", "");
              if (videoId) {
                const params = url.searchParams.toString();
                embedUrl = `https://www.youtube.com/embed/${videoId}${params ? `?${params}` : ""
                  }`;
              }
            }
          } catch {
            // Fallback to the raw URL if parsing fails
            embedUrl = videoExternalUrl;
          }

          content = (
            <div style={wrapperStyle}>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  maxWidth: "640px",
                  paddingBottom: "25%",
                  height: 0,
                  borderRadius: 4,
                  overflow: "hidden",
                }}
              >
                <iframe
                  className={className}
                  src={embedUrl}
                  title={alt || "Video"}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
          );
        } else if (videoSrc) {
          content = (
            <div style={wrapperStyle}>
              <video
                className={className}
                src={videoSrc}
                controls
                style={{
                  width: "100%",
                  maxWidth: "640px",
                  maxHeight: "360px",
                  height: "auto",
                  display: "block",
                  borderRadius: 4,
                }}
              />
            </div>
          );
        } else {
          content = null;
        }
      } else {
        const imgStyle: React.CSSProperties = { maxWidth: "100%", display: "block" };

        content = src ? (
          <div style={wrapperStyle}>
            {config.linkURL ? (
              <a href={config.linkURL} target={config.openNewTab ? "_blank" : "_self"} rel="noreferrer">
                <img className={className} src={src} alt={alt} style={imgStyle} />
              </a>
            ) : (
              <img className={className} src={src} alt={alt} style={imgStyle} />
            )}
          </div>
        ) : null;
      }
      break;
    }
    case "search_query": {
      if (config.searchQueryId && config.scope) {
        content = (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazySearchQueryRenderer
              searchQueryId={String(config.searchQueryId)}
              scope={config.scope}
              searchQueryName={config.searchQueryName}
              showPagination
              domain={elementDomain}
            />
          </React.Suspense>
        );
      } else {
        content = <span>No search query selected. Configure search query in settings.</span>;
      }
      break;
    }
    case "public_directory": {
      const publicDirectoryScope = config?.publicDirectorySetting?.scope;
      const publicDirectoryPayload = config?.publicDirectorySetting?.queryPayload;

      if (!publicDirectoryScope) {
        content = <span>No directory selected. Configure directory in settings.</span>;
        break;
      }

      content = (
        <React.Suspense fallback={<div>Loading...</div>}>
          <LazyPublicDirectoryRenderer
            isMultiColumn={isMultiColumn}
            key={`${element.id}-${publicDirectoryScope}-${JSON.stringify(publicDirectoryPayload ?? {})}`}
            scope={publicDirectoryScope}
            queryPayload={publicDirectoryPayload}
          />

        </React.Suspense>
      );
      break;
    }
    case "contact_summary": {
      return (
        <SummaryCardComponent
          summaryType={"contact_summary"}
          onProfileClick={() => console.log('Go to profile')}
        />
      );
    }

    case "organization_summary": {
      return (
        <SummaryCardComponent
          summaryType={"organization_summary"}
          onProfileClick={() => console.log('Go to profile')}
        />
      );
    }
    default: {
      content = <span>{element.accessKey}</span>;
      break;
    }
  }

  return <LayoutElement>{content}</LayoutElement>;
};

const AboutView = ({ page }: { page: AboutPage }) => (
  <>
    <BannerHero background={page.hero.image}>
      <BannerHeading>{page.hero.title}</BannerHeading>
      <Breadcrumb>
        {page.hero.breadcrumb.map((crumb, index) => (
          <span key={crumb}>
            {crumb}
            {index < page.hero.breadcrumb.length - 1 && <Chevron>›</Chevron>}
          </span>
        ))}
      </Breadcrumb>
    </BannerHero>

    <AboutGrid>
      <StoryCard>
        <SectionHeading as="h3">{page.story.heading}</SectionHeading>
        <BodyCopy>{page.story.body}</BodyCopy>
        <PrimaryButton>{page.story.ctaLabel}</PrimaryButton>
      </StoryCard>
      <StatsPanel>
        <StatsHeading>{page.statsPanel.heading}</StatsHeading>
        <BodyCopy>{page.statsPanel.description}</BodyCopy>
        <StatsGrid>
          {page.statsPanel.stats.map((stat) => (
            <StatCard key={stat.label}>
              <StatValue>{stat.value}</StatValue>
              <StatLabel>{stat.label}</StatLabel>
            </StatCard>
          ))}
        </StatsGrid>
      </StatsPanel>
    </AboutGrid>

    <FocusStrip>
      <FocusCopy>
        <FocusHeading>{page.focus.heading}</FocusHeading>
        <BodyCopy>{page.focus.description}</BodyCopy>
        <GhostButton>{page.focus.ctaLabel}</GhostButton>
      </FocusCopy>
      <FocusImage src={page.focus.image} alt={page.focus.heading} />
    </FocusStrip>

    <ReasonsSection>
      <SectionHeading>{page.reasons.heading}</SectionHeading>
      <SectionDescription>{page.reasons.subtitle}</SectionDescription>
      <ReasonCards>
        {page.reasons.items.map((reason) => (
          <ReasonCard key={reason.heading}>
            <ReasonHeading>{reason.heading}</ReasonHeading>
            <BodyCopy>{reason.body}</BodyCopy>
          </ReasonCard>
        ))}
      </ReasonCards>
    </ReasonsSection>
  </>
);

const EventsView = ({ page }: { page: EventsPage }) => (
  <>
    <BannerHero background={page.hero.image}>
      <BannerHeading>{page.hero.title}</BannerHeading>
      <Breadcrumb>
        {page.hero.breadcrumb.map((crumb, index) => (
          <span key={crumb}>
            {crumb}
            {index < page.hero.breadcrumb.length - 1 && <Chevron>›</Chevron>}
          </span>
        ))}
      </Breadcrumb>
    </BannerHero>

    <EventsIntro>
      <SectionHeading>{page.intro.heading}</SectionHeading>
      <SectionDescription>{page.intro.description}</SectionDescription>
    </EventsIntro>

    <EventsGrid>
      {page.eventCards.map((event) => (
        <EventCardShell key={event.title}>
          <EventImage src={event.image} alt={event.title} />
          <EventBody>
            <EventLocation>{event.location}</EventLocation>
            <EventTitle>{event.title}</EventTitle>
            <BodyCopy>{event.blurb}</BodyCopy>
            <EventFooter>
              <PrimaryButton>{event.priceLabel}</PrimaryButton>
              <AttendingLabel>Attending to this event</AttendingLabel>
            </EventFooter>
          </EventBody>
        </EventCardShell>
      ))}
    </EventsGrid>

    <StatsBar>
      {page.stats.map((stat) => (
        <StatCard key={stat.label}>
          <StatValue>{stat.value}</StatValue>
          <StatLabel>{stat.label}</StatLabel>
        </StatCard>
      ))}
    </StatsBar>

    <TableSection>
      <TableHeading>Upcoming Events In My Area</TableHeading>
      <BodyCopy>
        In today’s fast-paced world, your goals deserve the utmost attention and convenience. That’s why Luminous
        offers a suite of integrated services designed to cater to your professional needs digitally.
      </BodyCopy>
      <EventTable>
        <thead>
          <tr>
            <th>Event Name</th>
            <th>Event Starting Date</th>
            <th>Registration End Date</th>
            <th>Location</th>
            <th>Event Owner</th>
            <th>Event Fees</th>
          </tr>
        </thead>
        <tbody>
          {page.localEvents.map((localEvent) => (
            <tr key={localEvent.name}>
              <td>{localEvent.name}</td>
              <td>{localEvent.start}</td>
              <td>{localEvent.end}</td>
              <td>{localEvent.location}</td>
              <td>{localEvent.owner}</td>
              <td>{localEvent.fee}</td>
            </tr>
          ))}
        </tbody>
      </EventTable>
    </TableSection>
  </>
);

const WebsiteShell = styled.div`
  font-family: var(--brand-font);
  background: var(--surface-base);
  color: var(--text-primary);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const AuthBackdrop = styled.section`
  padding: 3.5rem 2.5rem 4.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ContactOnboardingGate = styled.section`
  padding: 1.5rem 2.5rem 2.5rem;
  background: #f7f8fb;
  min-height: 100vh;
`;

const ContactOnboardingIntro = styled.div`
  max-width: 1180px;
  margin: 0 auto 1.85rem;
`;

const ContactOnboardingIntroTitle = styled.h1`
  margin: 0;
  color: #003b77;
  font-size: 20px;
  line-height: 1.08;
  font-weight: 600;
`;

const ContactOnboardingIntroSubtitle = styled.p`
  margin: 0.55rem 0 0;
  color: #2b2f38;
  font-size: 15px;
  line-height: 1.2;
  font-weight: 400;
`;

const ContactOnboardingHeaderBar = styled.header`
  height: 72px;
  padding: 0 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 5;
  background: #fdfdff;
`;

const ContactOnboardingHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ContactOnboardingHeaderLogoImage = styled.img`
  height: 40px;
  width: auto;
  display: block;
`;

const ContactOnboardingHeaderRight = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ContactOnboardingProfileTriggerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: none;
  background: transparent;
  cursor: pointer;
`;

const ContactOnboardingProfileAvatarCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
`;

const ContactOnboardingProfileAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

const ContactOnboardingProfileTriggerText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const ContactOnboardingDropdownWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const ContactOnboardingCard = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 1.25rem;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 16px 40px rgba(10, 38, 64, 0.1);
`;

const AuthCard = styled.div`
  position: relative;
  max-width: 960px;
  width: 100%;
  border-radius: 4px;
  background: #ffffff;
  box-shadow: var(--shadow-card);
  overflow: hidden;
`;

const AuthInner = styled.div`
  padding: 3.5rem 3.75rem 3rem;
  text-align: center;
  color: #FF6D41;
`;

const AuthIllustrationCircle = styled.img`
  width: 120px;
  height: auto;
  display: block;
  margin: 0 auto 1.75rem;
`;

const AuthTitle = styled.h1`
  font-size: 2rem;
  margin: 0 0 0.5rem;
  color: var(--app-primary);
`;

const AuthSubtitle = styled.p`
  margin: 0;
  color: #585558;
  font-weight: 400;
`;

const AuthMessage = styled.p`
  margin: 1.25rem auto 0;
  max-width: 520px;
  font-size: 0.95rem;
  color: #585558;
  font-weight: 400;
`;

const AuthButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1.75rem;
`;

const AuthInfoCard = styled.div`
  margin: 2.5rem auto 0;
  max-width: 560px;
  padding: 1.5rem 1.75rem;
  border-radius: 4px;
  background: #fff7ea;
  text-align: left;
`;

const AuthInfoTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1rem;
  color: var(--app-primary);
`;

const AuthInfoList = styled.ul`
  margin: 0;
  padding-left: 1.1rem;
  font-size: 0.9rem;
  color: var(--text-secondary);
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const AuthBeamTopLeft = styled.div`
  position: absolute;
  top: -40px;
  left: 20%;
  width: 180px;
  height: 80px;
  background: linear-gradient(135deg, #ff7a45, rgba(255, 122, 69, 0));
  transform: rotate(-15deg);
`;

const AuthBeamTopRight = styled.div`
  position: absolute;
  top: -60px;
  right: 12%;
  width: 220px;
  height: 90px;
  background: linear-gradient(135deg, #2dd4bf, rgba(45, 212, 191, 0));
  transform: rotate(18deg);
`;

const AuthBeamBottomLeft = styled.div`
  position: absolute;
  bottom: -60px;
  left: 12%;
  width: 220px;
  height: 90px;
  background: linear-gradient(135deg, #2563eb, rgba(37, 99, 235, 0));
  transform: rotate(-18deg);
`;

const AuthBeamBottomRight = styled.div`
  position: absolute;
  bottom: -80px;
  right: 18%;
  width: 200px;
  height: 90px;
  background: linear-gradient(135deg, #34d399, rgba(52, 211, 153, 0));
  transform: rotate(12deg);
`;

const PageRowsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const LayoutRowShell = styled.section<{
  $bgColor?: string;
  $backgroundImageUrl?: string;
  $overlayColor?: string;
  $overlayOpacity?: number;
}>`
  position: relative;
  border-radius: 4px;
  padding: 3rem;
  margin: 0.75rem 1.5rem;
  background-color: ${({ $bgColor }) => $bgColor || "transparent"};
  background-image: ${({ $backgroundImageUrl }) =>
    $backgroundImageUrl ? `url(${$backgroundImageUrl})` : "none"};
  background-size: ${({ $backgroundImageUrl }) =>
    $backgroundImageUrl ? "cover" : "auto"};
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-color: ${({ $overlayColor }) => $overlayColor || "transparent"};
    opacity: ${({ $overlayOpacity }) =>
    typeof $overlayOpacity === "number" ? $overlayOpacity : 0};
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const SingleColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
`;

const LayoutColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const LayoutElement = styled.div<{ $isDisabled?: boolean }>`
  position: relative;
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.45 : 1)};
  pointer-events: ${({ $isDisabled }) => ($isDisabled ? "none" : "auto")};
  filter: ${({ $isDisabled }) => ($isDisabled ? "grayscale(0.2)" : "none")};
`;

const TopUtilityBar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem 2.5rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.85);
  background: #0b2c55;
`;

const UtilityBrand = styled.span`
  letter-spacing: 0.05em;
`;

const UtilityLinks = styled.div`
  display: flex;
  gap: 1.5rem;

  a {
    color: rgba(255, 255, 255, 0.85);
    text-decoration: none;
  }
`;

const NavigationBar = styled.nav<{ $isCenterStacked?: boolean }>`
  display: flex;
  align-items: center;
  padding: ${({ $isCenterStacked }) =>
    $isCenterStacked ? "2.25rem 2.5rem" : "1.25rem 2.5rem"};
  background: var(--surface-card);
  box-shadow: 0 25px 45px rgba(10, 38, 64, 0.08);
  position: sticky;
  top: 0;
  z-index: 1000;
  gap: 1.5rem;
  overflow: visible;
`;

const NavCenterStack = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  pointer-events: none;
  & > * { pointer-events: auto; }
`;

const BrandBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const BrandIcon = styled.div`
  height: 58px;
  max-width: 220px;
  border-radius: 4px;
  background: transparent;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
`;

const BrandLogo = styled.img`
  max-height: 100%;
  width: auto;
  object-fit: contain;
`;

const BrandTagline = styled.span`
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const NavItems = styled.div<{ $layout?: string }>`
  display: flex;
  gap: 1.5rem;
  ${({ $layout }) =>
    $layout === "center-stacked"
      ? "justify-content: center; width: 100%;"
      : ""}
`;

const NavItemWrapper = styled.div`
  position: relative;
`;

const NavLink = styled.a<{ $isActive: boolean }>`
  text-decoration: ${({ $isActive }) => ($isActive ? "underline" : "none")};
  text-underline-offset: 0.3rem;
  color: var(--nav-text-color);
  font-weight: ${({ $isActive }) => ($isActive ? 700 : 600)};
  position: relative;
  cursor: pointer;
  white-space: nowrap;
  padding: 0.25rem 0;

  &:after {
    position: absolute;
    left: 0;
    bottom: -6px;
    width: 100%;
    height: 2px;
    background: ${({ $isActive }) => ($isActive ? "var(--app-accent)" : "transparent")};
    transition: background 0.3s ease;
  }

  &:hover:after {
    background: var(--app-accent);
  }
`;

const DropdownPanel = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  padding-top: 0.5rem;
  background: var(--surface-card);
  padding: 0.5rem 0 0;
  border-radius: 4px;
  box-shadow: var(--shadow-card);
  border: 1px solid var(--border-subtle);
  display: flex;
  flex-direction: column;
  gap: 0rem;
  min-width: 180px;
  z-index: 20;
`;

const DropdownItem = styled.a`
  color: var(--nav-text-color);
  font-size: 0.95rem;
  font-weight: 500;
  text-decoration: none;
  padding: 0.5rem 1rem;
  cursor: pointer;
  background: var(--surface-card);
  transition: color 0.15s ease, background-color 0.15s ease;
  white-space: nowrap;

  &:hover {
    color: var(--app-primary);
    background-color: #f3f4f6;
  }
`;

const NavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
`;

const IconButton = styled.button`
  border: none;
  background: var(--surface-card);
  width: 42px;
  height: 42px;
  border-radius: 4px;
  display: grid;
  place-items: center;
  cursor: pointer;
`;

const NavButton = styled.button`
  padding: 0.7rem 1.4rem;
  border-radius: 4px;
  border: none;
  background: var(--app-primary);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
`;

const ProfileBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const ProfileInitials = styled.span`
  width: 44px;
  height: 44px;
  border-radius: 4px;
  background: var(--app-neutral);
  display: grid;
  place-items: center;
  font-weight: 600;
`;

const ProfileCopy = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProfileName = styled.span`
  font-weight: 600;
`;

const ProfileStatus = styled.span`
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

const NavDivider = styled.div`
  width: 1px;
  height: 28px;
  background: var(--border-subtle);
`;

const ProfileTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
`;

const ProfileInitialsCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: transparent;
  border: 1px solid var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: var(--text-secondary);
`;

const ProfileAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 999px;
  object-fit: cover;
`;

const ProfileTextStack = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProfileTriggerName = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-secondary);
`;

const ProfileChevron = styled.span`
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

const HeroSection = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 3rem;
  align-items: center;
  padding: 4rem 2.5rem;
  background: var(--hero-gradient);
  margin-top: 1.5rem;
  border-radius: 4px;
  margin-left: 2.5rem;
  margin-right: 2.5rem;
`;

const HeroCopy = styled.div`
  max-width: 520px;
`;

const HeroEyebrow = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--app-primary);
  font-size: 0.78rem;
  font-weight: 600;
`;

const HeroHeading = styled.h1`
  margin: 1rem 0;
  font-size: 3rem;
  line-height: 1.1;
  color: var(--app-primary);
`;

const HeroDescription = styled.p`
  color: var(--text-secondary);
`;

const HeroActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 4px;
  padding: 0.9rem 1.8rem;
  background: var(--app-primary);
  color: #fff;
  font-weight: 400;
  cursor: pointer;
  text-decoration: none; 
`;

const GhostButton = styled.button`
  border-radius: 4px;
  padding: 0.9rem 1.8rem;
  border: 1px solid var(--app-primary);
  background: transparent;
  color: var(--app-primary);
  font-weight: 400;
  cursor: pointer;
  text-decoration: none;
`;

const HeroImageWrapper = styled.div`
  border-radius: 4px;
  overflow: hidden;
  box-shadow: var(--shadow-card);
`;

const HeroImage = styled.img`
  width: 100%;
  display: block;
`;

const ServicesSection = styled.section`
  padding: 4rem 2.5rem;
  text-align: center;
`;

const SectionHeading = styled.h2`
  font-size: 2.2rem;
  color: var(--app-primary);
`;

const SectionDescription = styled.p`
  color: var(--text-secondary);
  max-width: 640px;
  margin: 1rem auto 2rem;
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.75rem;
`;

const ServiceCard = styled.article`
  background: var(--surface-card);
  border: 1px solid var(--border-subtle);
  border-radius: 4px;
  padding: 2rem;
  text-align: left;
  box-shadow: var(--shadow-card);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-8px);
  }
`;

const ServiceEmphasis = styled.span`
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--app-primary);
`;

const ServiceTitle = styled.h3`
  margin: 0.8rem 0;
  color: var(--app-primary);
`;

const ServiceDescription = styled.p`
  color: var(--text-secondary);
`;

const HighlightSection = styled.section<{ reversed: boolean }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 3rem;
  align-items: center;
  padding: 3rem 2.5rem;
  flex-direction: ${({ reversed }) => (reversed ? "row-reverse" : "row")};
`;

const HighlightImageWrapper = styled.div`
  border-radius: 4px;
  overflow: hidden;
  box-shadow: var(--shadow-card);
`;

const HighlightImage = styled.img`
  width: 100%;
  display: block;
`;

const HighlightCopy = styled.div`
  max-width: 520px;
`;

const HighlightEmphasis = styled.span`
  color: var(--app-accent);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const HighlightTitle = styled.h3`
  font-size: 2rem;
  margin: 0.8rem 0;
  color: var(--app-primary);
`;

const HighlightDescription = styled.p`
  color: var(--text-secondary);
`;

const TextLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--app-primary);
  font-weight: 600;
  text-decoration: none;
  margin-top: 1rem;
`;

const BannerHero = styled.section<{ background: string }>`
  position: relative;
  padding: 4rem 2.5rem;
  border-radius: 4px;
  margin: 2rem 2.5rem 0;
  color: #fff;
  overflow: hidden;
  background-image: ${({ background }) => `linear-gradient(rgba(7, 24, 45, 0.65), rgba(7, 24, 45, 0.65)), url(${background})`};
  background-size: cover;
  background-position: center;
`;

const BannerHeading = styled.h1`
  font-size: 3rem;
  margin: 0 0 0.5rem;
  color: var(--app-primary);
`;

const Breadcrumb = styled.div`
  display: flex;
  gap: 0.5rem;
  font-weight: 500;
`;

const Chevron = styled.span`
  margin: 0 0.35rem;
`;

const AboutGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  margin: 3rem 2.5rem;
`;

const StoryCard = styled.article`
  background: #fff;
  border-radius: 4px;
  padding: 2.5rem;
  box-shadow: var(--shadow-card);
`;

const BodyCopy = styled.p`
  color: var(--text-secondary);
`;

const StatsPanel = styled.article`
  background: var(--surface-card);
  border-radius: 4px;
  padding: 2.5rem;
  box-shadow: var(--shadow-card);
`;

const StatsHeading = styled.h3`
  margin-top: 0;
  color: var(--app-primary);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
  margin-top: 1.5rem;
`;

const StatCard = styled.div`
  background: #fff;
  border-radius: 4px;
  padding: 1.5rem;
  text-align: center;
  box-shadow: var(--shadow-card);
`;

const StatValue = styled.span`
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--app-primary);
`;

const StatLabel = styled.span`
  display: block;
  margin-top: 0.3rem;
  color: var(--text-secondary);
`;

const FocusStrip = styled.section`
  margin: 0 2.5rem;
  border-radius: 4px;
  padding: 2.5rem;
  background: #0c1b33;
  color: #fff;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  align-items: center;
`;

const FocusCopy = styled.div``;

const FocusHeading = styled.h3`
  font-size: 2rem;
  color: var(--app-primary);
`;

const FocusImage = styled.img`
  width: 100%;
  border-radius: 4px;
  border: 6px solid rgba(255, 255, 255, 0.12);
`;

const ReasonsSection = styled.section`
  padding: 4rem 2.5rem;
`;

const ReasonCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const ReasonCard = styled.article`
  background: #fff;
  border-radius: 4px;
  padding: 1.8rem;
  box-shadow: var(--shadow-card);
`;

const ReasonHeading = styled.h4`
  margin-bottom: 0.6rem;
  color: var(--app-primary);
`;

const EventsIntro = styled.section`
  padding: 3rem 2.5rem 1rem;
  text-align: center;
`;

const EventsGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin: 0 2.5rem 3rem;
`;

const EventCardShell = styled.article`
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: var(--shadow-card);
  display: flex;
  flex-direction: column;
`;

const EventImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
`;

const EventBody = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const EventLocation = styled.span`
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const EventTitle = styled.h3`
  margin: 0;
  color: var(--app-primary);
`;

const EventFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;

const AttendingLabel = styled.span`
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const StatsBar = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin: 0 2.5rem 3rem;
`;

const TableSection = styled.section`
  margin: 0 2.5rem 3rem;
  background: #fff;
  border-radius: 4px;
  padding: 2rem;
  box-shadow: var(--shadow-card);
`;

const TableHeading = styled.h3`
  margin-top: 0;
  color: var(--app-primary);
`;

const EventTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1.5rem;

  th,
  td {
    text-align: left;
    padding: 0.9rem;
    border-bottom: 1px solid var(--border-subtle);
  }

  th {
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
`;

// Shared responsive gap for footer columns; grows with viewport width
const FOOTER_COLUMNS_GAP = "clamp(2rem, 4.5vw, 20rem)";

const Footer = styled.footer`
  // margin: 4rem 2.5rem 0;
  padding: 1.5rem 2.5rem;
  background: var(--footer-bg);
  color: var(--footer-text);
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 0rem;
`;

const FooterTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  // justify-content: space-between;
  flex-wrap: wrap;
  gap: ${FOOTER_COLUMNS_GAP};
`;

const FooterLeftBlock = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 360px;
`;

const FooterBrandBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FooterLogo = styled.img`
  height: 56px;
  max-width: 220px;
  border-radius: 4px;
  object-fit: contain;
`;

const FooterBrandName = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
`;

const FooterTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FooterSummary = styled.p`
  font-size: 15px;
  margin: 0;
`;

const FooterSocialRow = styled.div`
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const FooterSocialIcon = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
    width: 32px;
    height: 32px;
    align-items: center;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.1);
    cursor: pointer;
    transition: 0.2s;
  &:hover {
    transform: translateY(-2px);
  }
`;

const FooterLinksRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const FooterColumnsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${FOOTER_COLUMNS_GAP};
  width: 100%;
`;

const FooterColumn = styled.div`
  display: inline-flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-right: 2.5rem;
`;

const FooterColumnHeading = styled.span`
  font-weight: 600;
`;

const FooterColumnItem = styled.span`
  color: var(--footer-text);

  a {
    color: inherit;
    text-decoration: none;
  }
`;

const LockedElementHint = styled.div`
  padding: 1.25rem 1.5rem;
  border-radius: 8px;
  border: 1px dashed rgba(15, 59, 112, 0.35);
  background: rgba(15, 59, 112, 0.04);
  display: inline-flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.9rem;
  color: var(--text-secondary);

  strong {
    font-weight: 600;
    color: var(--app-primary);
  }
`;

const FooterAddress = styled.div`
  max-width: 210px;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--footer-text);
  white-space: pre-line;
  margin-left: auto;
`;

const FooterLegal = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.9rem;
  color: var(--footer-text);
  margin-top: 20px;
  padding-top: 1rem;
  border-top: 1px solid rgb(235, 234, 235);
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 1rem;

  a {
    color: var(--footer-text);
    text-decoration: none;
  }
`;

const CookieConsentWrapper = styled.div`
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  width: min(520px, calc(100vw - 2rem));
  z-index: 1200;
  pointer-events: none;

  @media (max-width: 680px) {
    right: 0.75rem;
    bottom: 0.75rem;
    width: calc(100vw - 1.5rem);
  }
`;

const CookieConsentCard = styled.div`
  width: 100%;
  border-radius: 5px;
  border: 1px solid #f59e0b;
  background: #fdf7e9;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.12);
  padding: 1rem;
  pointer-events: auto;
`;

const CookieCardInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const CookieConsentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CookieConsentTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  line-height: 1.3;
  color: #111827;
  font-weight: 600;
`;

const CookieContentPad = styled.div`
  padding: 0 33px;
`;

const CookieConsentDescription = styled.p`
  margin: 0;
  color: #111827;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 400;
  max-width: 860px;
`;

const CookieConsentActions = styled.div`
  margin-top: 0.5rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;

  @media (max-width: 680px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const CookieActionButton = styled.button`
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 0.5rem 1.2rem;
  border-radius: 4px;
  border: none !important;
  background: #efefef !important;
  color: #111827 !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 1 !important;
  text-decoration: none !important;
  font-family: "Poppins", "Segoe UI", sans-serif !important;
  cursor: pointer;
`;

const CookieActionButtonPrimary = styled.button`
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 0.5rem 1.2rem;
  border-radius: 4px;
  border: none !important;
  background: #003b77 !important;
  color: #ffffff !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 1 !important;
  text-decoration: none !important;
  font-family: "Poppins", "Segoe UI", sans-serif !important;
  cursor: pointer;
`;

const StateBlock = styled.div`
  padding: 4rem;
  text-align: center;
  color: var(--text-secondary, #4b5567);
`;

export default WebsitePreview;
